import { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const MAIN_NAV = [
  { id: 'now', label: 'Now', icon: '🏠' },
  { id: 'tools', label: 'Tools', icon: '🧰' },
  { id: 'journal', label: 'Journal', icon: '📝' },
  { id: 'you', label: 'You', icon: '👤' }
];

const TOOL_TABS = [
  { id: 'breathe', label: 'Breathe', icon: '💨' },
  { id: 'calm', label: 'Calm', icon: '🌊' },
  { id: 'sounds', label: 'Sounds', icon: '🎧' },
  { id: 'focus', label: 'Focus', icon: '🎯' },
  { id: 'mood', label: 'Mood', icon: '🙂' },
  { id: 'meditate', label: 'Meditate', icon: '🧘' },
  { id: 'plan', label: 'Plan', icon: '✨' }
];

const NEED_OPTIONS = [
  { id: 'panic', label: 'Panic now', subtitle: 'Grounding & safety', emoji: '🆘', accent: 'rose' },
  { id: 'breathe', label: 'Slow down', subtitle: 'Guided breathwork', emoji: '🌬️', accent: 'teal' },
  { id: 'focus', label: 'Get one thing done', subtitle: 'ADHD-friendly focus', emoji: '🎯', accent: 'amber' },
  { id: 'journal', label: 'Untangle a thought', subtitle: 'CBT reframing', emoji: '📝', accent: 'violet' }
];

const BREATHING_TECHNIQUES = [
  {
    id: 'box',
    name: 'Box Breathing',
    subtitle: 'Calm anxiety and regain control',
    pattern: [
      { label: 'Inhale', seconds: 4, scale: 1.25, instruction: 'Breathe in slowly through your nose.' },
      { label: 'Hold', seconds: 4, scale: 1.25, instruction: 'Hold gently. Keep your shoulders soft.' },
      { label: 'Exhale', seconds: 4, scale: 0.75, instruction: 'Exhale slowly and smoothly.' },
      { label: 'Hold', seconds: 4, scale: 0.75, instruction: 'Pause calmly before the next breath.' }
    ],
    rounds: 6,
    color: 'cyan'
  },
  {
    id: '478',
    name: '4-7-8 Breathing',
    subtitle: 'Sleep, panic reduction and deep calm',
    pattern: [
      { label: 'Inhale', seconds: 4, scale: 1.25, instruction: 'Inhale quietly through your nose.' },
      { label: 'Hold', seconds: 7, scale: 1.25, instruction: 'Hold gently. Do not strain.' },
      { label: 'Exhale', seconds: 8, scale: 0.7, instruction: 'Exhale slowly through your mouth.' }
    ],
    rounds: 4,
    color: 'violet'
  },
  {
    id: 'sigh',
    name: 'Physiological Sigh',
    subtitle: 'Fast nervous system reset',
    pattern: [
      { label: 'Inhale', seconds: 2, scale: 1.15, instruction: 'Take a deep inhale.' },
      { label: 'Top-up', seconds: 1, scale: 1.32, instruction: 'Add a second small inhale.' },
      { label: 'Long Exhale', seconds: 6, scale: 0.65, instruction: 'Let out a long, slow exhale.' }
    ],
    rounds: 5,
    color: 'emerald'
  },
  {
    id: 'extended',
    name: 'Extended Exhale',
    subtitle: 'Reduce stress and slow the body down',
    pattern: [
      { label: 'Inhale', seconds: 4, scale: 1.2, instruction: 'Breathe in gently.' },
      { label: 'Exhale', seconds: 8, scale: 0.68, instruction: 'Make the exhale longer than the inhale.' }
    ],
    rounds: 6,
    color: 'blue'
  },
  {
    id: 'resonance',
    name: 'Resonance Breathing',
    subtitle: 'Steady rhythm for balance and HRV',
    pattern: [
      { label: 'Inhale', seconds: 5, scale: 1.22, instruction: 'Inhale with a smooth rhythm.' },
      { label: 'Exhale', seconds: 6, scale: 0.72, instruction: 'Exhale slowly and evenly.' }
    ],
    rounds: 8,
    color: 'teal'
  }
];

const MOODS = [
  { label: 'Anxious', technique: 'box' },
  { label: 'Panic', technique: 'sigh' },
  { label: 'Overwhelmed', technique: 'extended' },
  { label: 'Unfocused', technique: 'resonance' },
  { label: 'Can’t Sleep', technique: '478' },
  { label: 'Low Mood', technique: 'extended' }
];

const DISTORTIONS = [
  {
    id: 'catastrophising',
    name: 'Catastrophising',
    description: 'Assuming the worst-case scenario will happen.'
  },
  {
    id: 'mind-reading',
    name: 'Mind reading',
    description: 'Assuming you know what someone else thinks.'
  },
  {
    id: 'all-or-nothing',
    name: 'All-or-nothing thinking',
    description: 'Seeing things as perfect or terrible with no middle ground.'
  },
  {
    id: 'fortune-telling',
    name: 'Fortune telling',
    description: 'Predicting the future as if it is certain.'
  },
  {
    id: 'emotional-reasoning',
    name: 'Emotional reasoning',
    description: 'Believing something is true because it feels true.'
  },
  {
    id: 'overgeneralising',
    name: 'Overgeneralising',
    description: 'Using one event as proof of a permanent pattern.'
  },
  {
    id: 'should-statements',
    name: 'Should statements',
    description: 'Using harsh rules like “I should” or “I must”.'
  },
  {
    id: 'discounting-positive',
    name: 'Discounting positives',
    description: 'Ignoring evidence that things are not all bad.'
  }
];

const EMOTIONS = ['Anxious', 'Sad', 'Angry', 'Ashamed', 'Guilty', 'Overwhelmed', 'Hopeless', 'Frustrated', 'Lonely', 'Stressed'];

const PANIC_STEPS = [
  {
    title: 'Look around slowly',
    instruction: 'Name 5 things you can see. Say them slowly, out loud if possible.',
    prompt: '5 things I can see'
  },
  {
    title: 'Feel your body',
    instruction: 'Name 4 things you can feel: feet on floor, clothes, chair, air, hands.',
    prompt: '4 things I can feel'
  },
  {
    title: 'Listen carefully',
    instruction: 'Name 3 sounds you can hear, even tiny background sounds.',
    prompt: '3 things I can hear'
  },
  {
    title: 'Find scent or taste',
    instruction: 'Name 2 things you can smell, or 2 neutral smells you remember.',
    prompt: '2 things I can smell'
  },
  {
    title: 'One steady anchor',
    instruction: 'Name 1 thing you can taste or one sentence that feels grounding.',
    prompt: '1 grounding statement'
  }
];

const SAFETY_STATEMENTS = [
  'This is panic. It feels dangerous, but it will pass.',
  'My body is releasing adrenaline. I do not need to fight it.',
  'I can slow down. I only need to take the next breath.',
  'I am allowed to sit down, pause, and let this wave move through me.',
  'This feeling is temporary. I have survived this before.'
];

const FOCUS_PRESETS = [
  {
    id: 'quick-start',
    name: 'Quick Start',
    focusMinutes: 10,
    breakMinutes: 3,
    rounds: 1,
    description: 'For task initiation and ADHD resistance.'
  },
  {
    id: 'classic',
    name: 'Classic Sprint',
    focusMinutes: 25,
    breakMinutes: 5,
    rounds: 4,
    description: 'A balanced Pomodoro-style focus block.'
  },
  {
    id: 'deep-work',
    name: 'Deep Work',
    focusMinutes: 45,
    breakMinutes: 10,
    rounds: 2,
    description: 'Longer focus for absorbing tasks.'
  },
  {
    id: 'low-energy',
    name: 'Low Energy',
    focusMinutes: 5,
    breakMinutes: 2,
    rounds: 3,
    description: 'Tiny steps for low mood or overwhelm.'
  }
];

const FOCUS_TIPS = [
  'Make the task smaller than feels necessary.',
  'Only start. You do not have to finish everything.',
  'Put distractions in the parking lot, not in your head.',
  'Use the next 5 minutes to create momentum.',
  'Done imperfectly beats perfectly avoided.'
];

const MOOD_FACTORS = [
  { key: 'mood', label: 'Mood', emoji: '🙂', low: 'Low', high: 'Good' },
  { key: 'anxiety', label: 'Anxiety', emoji: '🌊', low: 'Calm', high: 'High' },
  { key: 'focus', label: 'Focus', emoji: '🎯', low: 'Scattered', high: 'Clear' },
  { key: 'energy', label: 'Energy', emoji: '⚡', low: 'Flat', high: 'Energised' },
  { key: 'sleep', label: 'Sleep', emoji: '🌙', low: 'Poor', high: 'Great' },
  { key: 'stress', label: 'Stress', emoji: '🔥', low: 'Low', high: 'High' }
];

const MOOD_TAGS = ['Work', 'Family', 'Health', 'Sleep', 'Exercise', 'Food', 'Social', 'Money', 'Weather', 'Medication', 'Overstimulation', 'Achievement'];

const MEDITATION_PRESETS = [
  {
    id: 'anxiety-calm',
    name: 'Anxiety Calm',
    minutes: 5,
    ambience: 'rain',
    description: 'Slow grounding guidance for anxiety and overwhelm.',
    script: [
      'Sit comfortably and allow your shoulders to drop.',
      'Notice the contact between your body and the surface beneath you.',
      'Let the exhale be slightly longer than the inhale.',
      'When thoughts appear, label them thinking and return to the breath.',
      'Remind yourself: I am here, I am breathing, this moment is manageable.'
    ]
  },
  {
    id: 'adhd-focus',
    name: 'ADHD Focus Reset',
    minutes: 7,
    ambience: 'brown',
    description: 'A short reset to settle restlessness before focus work.',
    script: [
      'Let your eyes rest on one point or gently close them.',
      'Feel your feet, your hands, and your breath.',
      'You do not need to fix every thought. Let them pass through.',
      'Choose one next action. One task. One step.',
      'Bring your attention back to the body whenever it wanders.'
    ]
  },
  {
    id: 'body-scan',
    name: 'Body Scan',
    minutes: 10,
    ambience: 'ocean',
    description: 'Progressive body awareness for tension release.',
    script: [
      'Begin at the top of your head and soften your forehead.',
      'Release the jaw, tongue and throat.',
      'Let your shoulders become heavy.',
      'Notice your chest, belly, hips and legs.',
      'Allow the whole body to rest as one calm system.'
    ]
  },
  {
    id: 'sleep-wind-down',
    name: 'Sleep Wind Down',
    minutes: 15,
    ambience: 'pink',
    description: 'Gentle evening guidance for sleep preparation.',
    script: [
      'There is nothing more to solve tonight.',
      'Let the day move into the background.',
      'Soften your breath and let your body sink down.',
      'If thoughts return, imagine placing them on a shelf for tomorrow.',
      'Rest is productive. You are allowed to stop.'
    ]
  }
];

const AMBIENCE_OPTIONS = [
  { id: 'rain', name: 'Gentle Rain', emoji: '🌧️', frequency: 220, category: 'Nature', description: 'Soft rainfall for calm focus.', file: 'assets/audio/gentle-rain.mp3' },
  { id: 'ocean', name: 'Ocean Waves', emoji: '🌊', frequency: 174, category: 'Nature', description: 'Slow rolling surf and sea air.', file: 'assets/audio/ocean-waves.mp3' },
  { id: 'thunder', name: 'Distant Storm', emoji: '⛈️', frequency: 98, category: 'Nature', description: 'Rain with occasional low rumble.' },
  { id: 'forest', name: 'Forest Morning', emoji: '🌲', frequency: 196, category: 'Nature', description: 'Woodland hush with gentle movement.', file: 'assets/audio/forest-morning.mp3' },
  { id: 'stream', name: 'Mountain Stream', emoji: '💧', frequency: 262, category: 'Nature', description: 'Bright flowing water.', file: 'assets/audio/mountain-stream.mp3' },
  { id: 'wind', name: 'Soft Wind', emoji: '🍃', frequency: 220, category: 'Nature', description: 'Airy breeze through open space.', file: 'assets/audio/soft-wind.mp3' },
  { id: 'fireplace', name: 'Fireplace', emoji: '🔥', frequency: 146, category: 'Cozy', description: 'Warm crackling hearth.', file: 'assets/audio/fireplace.mp3' },
  { id: 'night', name: 'Night Sky', emoji: '🌙', frequency: 110, category: 'Sleep', description: 'Deep quiet for winding down.', file: 'assets/audio/night-after-rain.mp3' },
  { id: 'brown', name: 'Brown Noise', emoji: '🟤', frequency: 110, category: 'Focus', description: 'Low, grounding masking tone.' },
  { id: 'pink', name: 'Pink Noise', emoji: '🌸', frequency: 196, category: 'Focus', description: 'Balanced hiss for concentration.' },
  { id: 'white', name: 'White Noise', emoji: '⬜', frequency: 200, category: 'Focus', description: 'Steady neutral masking sound.' },
  { id: 'zen', name: 'Zen Drone', emoji: '🪷', frequency: 136, category: 'Meditation', description: 'Pure sustaining tone, minimal texture.' },
  { id: 'healing', name: 'Healing Tone', emoji: '✨', frequency: 528, category: 'Meditation', description: 'Bright harmonic hum.' },
  { id: 'none', name: 'Silent', emoji: '🔕', frequency: 0, category: '—', description: 'No background audio.' }
];

const EMOTIONAL_STATES = [
  { id: 'anxious', label: 'Anxious', emoji: '🌊' },
  { id: 'panic', label: 'Panic', emoji: '🚨' },
  { id: 'overwhelmed', label: 'Overwhelmed', emoji: '🌀' },
  { id: 'unfocused', label: 'Unfocused', emoji: '🎯' },
  { id: 'low-mood', label: 'Low Mood', emoji: '☁️' },
  { id: 'angry', label: 'Angry', emoji: '🔥' },
  { id: 'tired', label: 'Tired', emoji: '🌙' },
  { id: 'overstimulated', label: 'Overstimulated', emoji: '⚡' }
];

const ROUTINE_GOALS = [
  'Calm down fast',
  'Focus on a task',
  'Stop spiralling thoughts',
  'Prepare for sleep',
  'Lift my mood gently',
  'Recover from overwhelm'
];

const SUPPORT_STYLES = ['Gentle', 'Direct', 'Practical', 'Very low stimulation'];

const ROUTINE_LIBRARY = {
  anxious: [
    { tool: 'Physiological Sigh', type: 'Breathwork', minutes: 2, action: 'Take 3 double-inhale long-exhale breaths.' },
    { tool: 'Box Breathing', type: 'Breathwork', minutes: 4, action: 'Follow a 4-4-4-4 breathing rhythm.' },
    { tool: 'CBT Check', type: 'CBT', minutes: 5, action: 'Write the automatic thought and one balanced alternative.' },
    { tool: 'Anxiety Calm', type: 'Meditation', minutes: 5, action: 'Use the anxiety meditation player.' }
  ],
  panic: [
    { tool: 'Sit Down + Orient', type: 'Safety', minutes: 1, action: 'Sit down, look around, and remind yourself this is panic.' },
    { tool: 'Physiological Sigh', type: 'Breathwork', minutes: 2, action: 'Do 3–5 physiological sighs slowly.' },
    { tool: '5-4-3-2-1 Grounding', type: 'Grounding', minutes: 5, action: 'Name things you see, feel, hear, smell and taste.' },
    { tool: 'Safety Statement', type: 'Reassurance', minutes: 1, action: 'Repeat: this feeling is temporary and will pass.' }
  ],
  overwhelmed: [
    { tool: 'Brain Dump', type: 'ADHD', minutes: 4, action: 'Write every loose thought without organising it.' },
    { tool: 'Extended Exhale', type: 'Breathwork', minutes: 4, action: 'Breathe in for 4, out for 8.' },
    { tool: 'One Thing Choice', type: 'Focus', minutes: 2, action: 'Pick one tiny next action.' },
    { tool: 'Quick Start Timer', type: 'ADHD', minutes: 10, action: 'Start a 10-minute focus sprint.' }
  ],
  unfocused: [
    { tool: 'ADHD Focus Reset', type: 'Meditation', minutes: 7, action: 'Use the ADHD focus reset meditation.' },
    { tool: 'Task Intention', type: 'Focus', minutes: 2, action: 'Write one task only.' },
    { tool: 'Quick Start Timer', type: 'ADHD', minutes: 10, action: 'Work for 10 minutes, then stop or continue.' },
    { tool: 'Distraction Parking', type: 'ADHD', minutes: 1, action: 'Park distractions instead of following them.' }
  ],
  'low-mood': [
    { tool: 'Tiny Step', type: 'Behavioural Activation', minutes: 3, action: 'Choose one tiny action: water, curtains, shower or outside air.' },
    { tool: 'Mood Check-In', type: 'Mood Tracking', minutes: 3, action: 'Record mood, energy and one support need.' },
    { tool: 'Self Compassion Prompt', type: 'CBT', minutes: 4, action: 'Write what you would say to a friend.' },
    { tool: 'Low Energy Timer', type: 'ADHD', minutes: 5, action: 'Do one 5-minute starter task.' }
  ],
  angry: [
    { tool: 'Long Exhale', type: 'Breathwork', minutes: 4, action: 'Use longer exhales to reduce activation.' },
    { tool: 'Body Scan', type: 'Meditation', minutes: 10, action: 'Notice where anger sits in the body.' },
    { tool: 'CBT Pause', type: 'CBT', minutes: 5, action: 'Write the urge, consequence and wiser response.' }
  ],
  tired: [
    { tool: 'Sleep Wind Down', type: 'Meditation', minutes: 15, action: 'Start the sleep wind-down meditation.' },
    { tool: '4-7-8 Breathing', type: 'Breathwork', minutes: 4, action: 'Use slow 4-7-8 breathing.' },
    { tool: 'Low Stimulation Reset', type: 'Environment', minutes: 2, action: 'Dim lights and reduce noise.' }
  ],
  overstimulated: [
    { tool: 'Low Stimulation Mode', type: 'Environment', minutes: 2, action: 'Reduce light, sound and notifications.' },
    { tool: 'Grounding', type: 'Grounding', minutes: 5, action: 'Feel feet, hands and one object texture.' },
    { tool: 'Extended Exhale', type: 'Breathwork', minutes: 4, action: 'Breathe out longer than in.' },
    { tool: 'Anxiety Calm', type: 'Meditation', minutes: 5, action: 'Use a short calming meditation.' }
  ]
};

const DEFAULT_USER = {
  id: 'guest',
  name: 'Guest User',
  email: '',
  plan: 'Free',
  avatar: '🌿',
  createdAt: new Date().toISOString(),
  preferences: {
    lowStimulation: false,
    haptics: true,
    reminderTime: '09:00',
    emergencyContact: '',
    dataSync: false
  }
};

const PWA_MANIFEST = {
  name: 'Mind Wanders — Mental Wellness',
  short_name: 'Mind Wanders',
  description: 'Breathwork, CBT, ADHD focus, meditation and mood tracking tools.',
  start_url: './index.html',
  display: 'standalone',
  background_color: '#fafcff',
  theme_color: '#fafcff',
  orientation: 'portrait',
  categories: ['health', 'wellness', 'productivity'],
  icons: [
    {
      src: 'icon.png',
      sizes: '512x512',
      type: 'image/png'
    }
  ]
};

const STORAGE_CONSENT_KEY = 'mindflow-storage-consent';
const BREATH_SESSIONS_KEY = 'mindflow-breath-sessions';

const LOCAL_STORAGE_KEYS = [
  'mindflow-current-user',
  'mindflow-cbt-entries',
  'mindflow-mood-entries',
  'mindflow-meditation-sessions',
  'mindflow-ai-routines',
  'mindflow-offline-queue',
  'mindflow-last-sync-preview',
  'mindflow-theme',
  BREATH_SESSIONS_KEY
];

const DATA_STORAGE_ITEMS = [
  { label: 'Profile details', storage: 'This browser only', detail: 'Display name, email field, avatar, emergency contact, reminder time, and app preferences.' },
  { label: 'CBT journal entries', storage: 'This browser only', detail: 'Thought records and any text you type into the journal tools.' },
  { label: 'Mood check-ins', storage: 'This browser only', detail: 'Mood, anxiety, focus, energy, sleep, stress ratings, tags, notes, wins, and support notes.' },
  { label: 'Meditation and routine history', storage: 'This browser only', detail: 'Completed meditation sessions and saved generated routines.' },
  { label: 'Offline queue and sync preview', storage: 'This browser only', detail: 'Temporary local copies prepared for a future backend. No cloud sync is active yet.' },
  { label: 'Theme preference', storage: 'This browser only', detail: 'Light or dark mode preference.' },
  { label: 'Audio/PWA cache', storage: 'Browser cache', detail: 'App files and ambient audio may be cached so the app loads faster and can work offline.' }
];

const OFFLINE_FEATURES = [
  'Breathing sessions work offline',
  'CBT journal saves locally',
  'Mood tracking saves locally',
  'Meditation timer works offline',
  'ADHD focus timers work offline',
  'AI routines save locally'
];

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function playSoftTone(frequency, duration = 0.45, peakVolume = 0.14, audioContext) {
  try {
    const context = audioContext;
    if (!context) return;

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    gain.connect(context.destination);

    const now = context.currentTime;
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(peakVolume, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.08);
  } catch (error) {
    // Optional audio cue.
  }
}

function breathPhaseFrequency(label) {
  const normalized = String(label || '').toLowerCase();
  if (normalized.includes('inhale') || normalized.includes('top-up')) return 392;
  if (normalized.includes('hold')) return 330;
  if (normalized.includes('exhale')) return 262;
  return 349;
}

function colourClasses(colour) {
  const map = {
    cyan: {
      text: 'text-teal-700',
      bg: 'bg-teal-300',
      soft: 'bg-teal-100',
      border: 'border-teal-200',
      ring: 'shadow-teal-200/80'
    },
    violet: {
      text: 'text-violet-700',
      bg: 'bg-violet-300',
      soft: 'bg-violet-100',
      border: 'border-violet-200',
      ring: 'shadow-violet-200/80'
    },
    emerald: {
      text: 'text-emerald-700',
      bg: 'bg-emerald-300',
      soft: 'bg-emerald-100',
      border: 'border-emerald-200',
      ring: 'shadow-emerald-200/80'
    },
    blue: {
      text: 'text-sky-700',
      bg: 'bg-sky-300',
      soft: 'bg-sky-100',
      border: 'border-sky-200',
      ring: 'shadow-sky-200/80'
    },
    teal: {
      text: 'text-teal-700',
      bg: 'bg-teal-300',
      soft: 'bg-teal-100',
      border: 'border-teal-200',
      ring: 'shadow-teal-200/80'
    }
  };

  return map[colour] || map.cyan;
}

function emptyJournal() {
  return {
    situation: '',
    automaticThought: '',
    emotions: [],
    intensityBefore: 70,
    evidenceFor: '',
    evidenceAgainst: '',
    distortions: [],
    balancedThought: '',
    actionStep: '',
    intensityAfter: 35
  };
}

function emptyMoodCheckIn() {
  return {
    mood: 55,
    anxiety: 45,
    focus: 50,
    energy: 50,
    sleep: 50,
    stress: 45,
    tags: [],
    note: '',
    win: '',
    support: ''
  };
}

function averageValue(items, key) {
  if (!items.length) return 0;
  return Math.round(items.reduce((sum, item) => sum + Number(item[key] || 0), 0) / items.length);
}

function weekAgoTimestamp() {
  return Date.now() - 7 * 24 * 60 * 60 * 1000;
}

function computeWeeklyInsights(moodEntries, cbtEntries, meditationSessions, breathSessions) {
  const cutoff = weekAgoTimestamp();
  const inWeek = (iso) => new Date(iso).getTime() >= cutoff;
  const recentMoods = moodEntries.filter((entry) => inWeek(entry.createdAt || entry.updatedAt));
  const recentCbt = cbtEntries.filter((entry) => inWeek(entry.createdAt || entry.updatedAt));
  const recentMeditations = meditationSessions.filter((entry) => inWeek(entry.completedAt));

  const moodAvg = averageValue(recentMoods, 'mood');
  const anxietyAvg = averageValue(recentMoods, 'anxiety');
  const firstMood = recentMoods.length >= 2 ? recentMoods[recentMoods.length - 1].mood : null;
  const lastMood = recentMoods.length >= 2 ? recentMoods[0].mood : null;
  const moodDelta = firstMood != null && lastMood != null ? lastMood - firstMood : 0;

  return {
    checkIns: recentMoods.length,
    cbtEntries: recentCbt.length,
    meditations: recentMeditations.length,
    breathSessions,
    moodAvg,
    anxietyAvg,
    moodDelta
  };
}

function suggestBalancedThought(journal) {
  const parts = [];
  const thought = journal.automaticThought.trim();

  if (thought) {
    parts.push(`I'm having the thought that "${thought}", but feelings aren't facts.`);
  }

  if (journal.evidenceAgainst.trim()) {
    parts.push(journal.evidenceAgainst.trim());
  }

  if (journal.distortions.length) {
    const names = journal.distortions
      .map((id) => DISTORTIONS.find((item) => item.id === id)?.name)
      .filter(Boolean);
    if (names.length) {
      parts.push(`This may involve ${names.join(' and ').toLowerCase()} — a common thinking trap I can question.`);
    }
  }

  if (journal.evidenceFor.trim()) {
    parts.push(`Some evidence exists, but it may not tell the whole story.`);
  }

  if (parts.length === 0) {
    return "I'm feeling stressed, but I can take one small step. I don't need to solve everything right now.";
  }

  return parts.join(' ');
}

function vibratePhase(enabled) {
  if (!enabled) return;

  if (Capacitor.isNativePlatform()) {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    return;
  }

  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try {
    navigator.vibrate(12);
  } catch (error) {
    // Optional haptic.
  }
}

function useFocusTrap(active, containerRef) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;

    const container = containerRef.current;
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    function handleKeyDown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef]);
}

export default function MindFlowApp() {
  const [selectedId, setSelectedId] = useState('box');
  const [isRunning, setIsRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [round, setRound] = useState(1);
  const [secondsLeft, setSecondsLeft] = useState(BREATHING_TECHNIQUES[0].pattern[0].seconds);
  const [completed, setCompleted] = useState(false);
  const [journal, setJournal] = useState(emptyJournal());
  const [entries, setEntries] = useState([]);
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [panicModeOpen, setPanicModeOpen] = useState(false);
  const [panicStep, setPanicStep] = useState(0);
  const [panicIntensity, setPanicIntensity] = useState(85);
  const [panicNotes, setPanicNotes] = useState({});
  const [panicStatementIndex, setPanicStatementIndex] = useState(0);
  const [focusPresetId, setFocusPresetId] = useState('quick-start');
  const [focusMode, setFocusMode] = useState('focus');
  const [focusRunning, setFocusRunning] = useState(false);
  const [focusRound, setFocusRound] = useState(1);
  const [focusSecondsLeft, setFocusSecondsLeft] = useState(FOCUS_PRESETS[0].focusMinutes * 60);
  const [focusTask, setFocusTask] = useState('');
  const [focusDistraction, setFocusDistraction] = useState('');
  const [focusDistractions, setFocusDistractions] = useState([]);
  const [focusCompleted, setFocusCompleted] = useState(0);
  const [focusTipIndex, setFocusTipIndex] = useState(0);
  const [moodCheckIn, setMoodCheckIn] = useState(emptyMoodCheckIn());
  const [moodEntries, setMoodEntries] = useState([]);
  const [activeMoodId, setActiveMoodId] = useState(null);
  const [meditationId, setMeditationId] = useState('anxiety-calm');
  const [meditationRunning, setMeditationRunning] = useState(false);
  const [meditationSecondsLeft, setMeditationSecondsLeft] = useState(MEDITATION_PRESETS[0].minutes * 60);
  const [meditationVolume, setMeditationVolume] = useState(35);
  const [meditationBellEvery, setMeditationBellEvery] = useState(2);
  const [meditationSessions, setMeditationSessions] = useState([]);
  const [meditationScriptIndex, setMeditationScriptIndex] = useState(0);
  const [routineState, setRoutineState] = useState('anxious');
  const [routineGoal, setRoutineGoal] = useState('Calm down fast');
  const [routineMinutes, setRoutineMinutes] = useState(10);
  const [routineIntensity, setRoutineIntensity] = useState(70);
  const [routineSupportStyle, setRoutineSupportStyle] = useState('Gentle');
  const [routineSaved, setRoutineSaved] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [currentUser, setCurrentUser] = useState(DEFAULT_USER);
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [profileForm, setProfileForm] = useState(DEFAULT_USER);
  const [authMessage, setAuthMessage] = useState('');
  const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [swStatus, setSwStatus] = useState('Not registered');
  const [mainNav, setMainNav] = useState('now');
  const [activeTool, setActiveTool] = useState('breathe');
  const [breathSessionsCompleted, setBreathSessionsCompleted] = useState(0);
  const [breathPhaseAnnouncement, setBreathPhaseAnnouncement] = useState('');
  const [routineRunnerOpen, setRoutineRunnerOpen] = useState(false);
  const [routineRunnerSteps, setRoutineRunnerSteps] = useState([]);
  const [routineRunnerStepIndex, setRoutineRunnerStepIndex] = useState(0);
  const [routineRunnerSecondsLeft, setRoutineRunnerSecondsLeft] = useState(0);
  const [routineRunnerRunning, setRoutineRunnerRunning] = useState(false);
  const [reframeMessage, setReframeMessage] = useState('');
  const authModalRef = useRef(null);
  const panicModalRef = useRef(null);
  const routineRunnerRef = useRef(null);
  const [storageConsent, setStorageConsent] = useState(() => {
    if (typeof window === 'undefined') return 'pending';
    return window.localStorage?.getItem(STORAGE_CONSENT_KEY) || 'pending';
  });
  const [privacyMessage, setPrivacyMessage] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const savedTheme = window.localStorage?.getItem(STORAGE_CONSENT_KEY) === 'accepted'
      ? window.localStorage?.getItem('mindflow-theme')
      : null;
    if (savedTheme) return savedTheme === 'dark';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
  });
  const [breathAmbienceEnabled, setBreathAmbienceEnabled] = useState(true);
  const [ambienceTrackId, setAmbienceTrackId] = useState('ocean');
  const [soundLibraryPlaying, setSoundLibraryPlaying] = useState(false);
  const ambienceNodesRef = useRef([]);
  const ambienceMasterGainRef = useRef(null);
  const activeAmbienceIdRef = useRef(null);
  const ambienceCrackleRef = useRef(null);
  const ambienceAudioRef = useRef(null);
  const audioRef = useRef(null);
  const lastBreathToneRef = useRef('');
  const lastBreathCompletedRef = useRef(false);

  const selected = useMemo(
    () => BREATHING_TECHNIQUES.find((item) => item.id === selectedId) || BREATHING_TECHNIQUES[0],
    [selectedId]
  );

  const phase = selected.pattern[phaseIndex];
  const colours = colourClasses(selected.color);
  const totalSessionSeconds = selected.pattern.reduce((sum, item) => sum + item.seconds, 0) * selected.rounds;
  const elapsedRounds = round - 1;
  const previousPhaseSeconds = selected.pattern
    .slice(0, phaseIndex)
    .reduce((sum, item) => sum + item.seconds, 0);
  const currentPhaseElapsed = phase.seconds - secondsLeft;
  const elapsedSeconds = elapsedRounds * selected.pattern.reduce((sum, item) => sum + item.seconds, 0) + previousPhaseSeconds + currentPhaseElapsed;
  const progress = Math.min(100, Math.max(0, (elapsedSeconds / totalSessionSeconds) * 100));
  const selectedFocusPreset = useMemo(
    () => FOCUS_PRESETS.find((item) => item.id === focusPresetId) || FOCUS_PRESETS[0],
    [focusPresetId]
  );
  const focusTotalSeconds = focusMode === 'focus'
    ? selectedFocusPreset.focusMinutes * 60
    : selectedFocusPreset.breakMinutes * 60;
  const focusProgress = Math.min(100, Math.max(0, ((focusTotalSeconds - focusSecondsLeft) / focusTotalSeconds) * 100));
  const focusSessionLabel = focusMode === 'focus' ? 'Focus Sprint' : 'Recovery Break';
  const focusTip = FOCUS_TIPS[focusTipIndex % FOCUS_TIPS.length];
  const recentMoodEntries = moodEntries.slice(0, 7);
  const moodAverages = MOOD_FACTORS.reduce((acc, factor) => {
    acc[factor.key] = averageValue(recentMoodEntries, factor.key);
    return acc;
  }, {});
  const latestMood = moodEntries[0];
  const previousMood = moodEntries[1];
  const moodTrend = latestMood && previousMood ? latestMood.mood - previousMood.mood : 0;
  const anxietyTrend = latestMood && previousMood ? previousMood.anxiety - latestMood.anxiety : 0;
  const selectedMeditation = useMemo(
    () => MEDITATION_PRESETS.find((item) => item.id === meditationId) || MEDITATION_PRESETS[0],
    [meditationId]
  );
  const selectedAmbience = AMBIENCE_OPTIONS.find((item) => item.id === ambienceTrackId) || AMBIENCE_OPTIONS[0];
  const listenableTracks = AMBIENCE_OPTIONS.filter((item) => item.id !== 'none');
  const canStoreUserData = storageConsent === 'accepted';
  const meditationTotalSeconds = selectedMeditation.minutes * 60;
  const meditationProgress = Math.min(100, Math.max(0, ((meditationTotalSeconds - meditationSecondsLeft) / meditationTotalSeconds) * 100));
  const currentMeditationPrompt = selectedMeditation.script[meditationScriptIndex % selectedMeditation.script.length];
  const generatedRoutine = useMemo(() => {
    const base = ROUTINE_LIBRARY[routineState] || ROUTINE_LIBRARY.anxious;
    let remaining = routineMinutes;
    const steps = [];

    base.forEach((step) => {
      if (remaining <= 0) return;
      const adjustedMinutes = Math.min(step.minutes, remaining);
      steps.push({ ...step, minutes: adjustedMinutes });
      remaining -= adjustedMinutes;
    });

    if (routineIntensity >= 80 && !steps.some((step) => step.type === 'Safety')) {
      steps.unshift({
        tool: 'Safety Pause',
        type: 'Safety',
        minutes: 1,
        action: 'Sit down, slow your breathing, and remind yourself you only need the next step.'
      });
    }

    return steps;
  }, [routineState, routineMinutes, routineIntensity]);

  const routineTotalMinutes = generatedRoutine.reduce((sum, step) => sum + step.minutes, 0);
  const routineStateLabel = EMOTIONAL_STATES.find((item) => item.id === routineState)?.label || 'Anxious';
  const journalReduction = Math.max(0, journal.intensityBefore - journal.intensityAfter);
  const journalProgress = Math.round(
    ([journal.situation, journal.automaticThought, journal.evidenceFor, journal.evidenceAgainst, journal.balancedThought, journal.actionStep]
      .filter(Boolean).length / 6) * 100
  );

  const lowStimulation = Boolean(profileForm.preferences?.lowStimulation);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const calmMotion = !(lowStimulation || prefersReducedMotion);
  const hapticsEnabled = Boolean(profileForm.preferences?.haptics !== false) && calmMotion;

  const isImmersive =
    (activeTool === 'breathe' && isRunning) ||
    (activeTool === 'meditate' && meditationRunning) ||
    (activeTool === 'focus' && focusRunning);

  const weeklyInsights = useMemo(
    () => computeWeeklyInsights(moodEntries, entries, meditationSessions, breathSessionsCompleted),
    [moodEntries, entries, meditationSessions, breathSessionsCompleted]
  );

  const overlayPanelClass = isImmersive
    ? 'app-overlay-panel app-overlay-panel--fullscreen'
    : 'app-overlay-panel';

  useEffect(() => {
    const root = document.documentElement;

    function updateChromeHeights() {
      const crisis = document.getElementById('crisis-banner');
      if (crisis) {
        root.style.setProperty('--crisis-banner-height', `${crisis.offsetHeight}px`);
      }

      const nav = document.querySelector('[aria-label="Main navigation"]');
      const navHeight = !isImmersive && nav ? nav.getBoundingClientRect().height : 0;
      root.style.setProperty('--bottom-nav-height', `${navHeight}px`);
    }

    updateChromeHeights();
    window.addEventListener('resize', updateChromeHeights);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateChromeHeights) : null;
    const crisis = document.getElementById('crisis-banner');
    if (observer && crisis) observer.observe(crisis);

    const observeNav = () => {
      const nav = document.querySelector('[aria-label="Main navigation"]');
      if (observer && nav) observer.observe(nav);
    };
    observeNav();
    const navTimer = window.setTimeout(observeNav, 0);

    return () => {
      window.removeEventListener('resize', updateChromeHeights);
      window.clearTimeout(navTimer);
      observer?.disconnect();
    };
  }, [isImmersive, mainNav, storageConsent, authOpen, panicModeOpen]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, [mainNav]);

  const routineRunnerStep = routineRunnerSteps[routineRunnerStepIndex] || null;

  useEffect(() => {
    setPhaseIndex(0);
    setRound(1);
    setSecondsLeft(selected.pattern[0].seconds);
    setIsRunning(false);
    setCompleted(false);
  }, [selectedId, selected.pattern]);

  useEffect(() => {
    setMeditationRunning(false);
    setMeditationSecondsLeft(selectedMeditation.minutes * 60);
    setMeditationScriptIndex(0);
    if (selectedMeditation.ambience && selectedMeditation.ambience !== 'none') {
      setAmbienceTrackId(selectedMeditation.ambience);
    }
    stopAmbience();
  }, [selectedMeditation]);

  useEffect(() => {
    if (!meditationRunning) return undefined;

    const timer = window.setInterval(() => {
      setMeditationSecondsLeft((current) => {
        if (current > 1) {
          const next = current - 1;
          const elapsed = meditationTotalSeconds - next;
          const scriptStep = Math.floor((elapsed / meditationTotalSeconds) * selectedMeditation.script.length);
          setMeditationScriptIndex(Math.min(selectedMeditation.script.length - 1, scriptStep));

          if (meditationBellEvery > 0 && elapsed > 0 && elapsed % (meditationBellEvery * 60) === 0) {
            playMeditationBell();
          }

          return next;
        }

        completeMeditationSession();
        return 0;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [meditationRunning, meditationTotalSeconds, selectedMeditation, meditationBellEvery, meditationVolume]);

  useEffect(() => {
    let activeId = null;

    if (meditationRunning && selectedMeditation.ambience !== 'none') {
      activeId = ambienceTrackId || selectedMeditation.ambience;
    } else if (isRunning && !completed && breathAmbienceEnabled) {
      activeId = ambienceTrackId;
    } else if (soundLibraryPlaying) {
      activeId = ambienceTrackId;
    }

    if (!activeId || activeId === 'none') {
      stopAmbience();
      return undefined;
    }

    startTranquilAmbience(activeId, meditationVolume);

    return () => stopAmbience();
  }, [
    meditationRunning,
    selectedMeditation.ambience,
    isRunning,
    completed,
    breathAmbienceEnabled,
    soundLibraryPlaying,
    ambienceTrackId,
    meditationVolume
  ]);

  useEffect(() => {
    setAmbienceVolume(meditationVolume);
  }, [meditationVolume]);

  useEffect(() => {
    function updateOnlineStatus() {
      setIsOnline(navigator.onLine);
    }

    function captureInstallPrompt(event) {
      event.preventDefault();
      setInstallPrompt(event);
      setIsInstallable(true);
    }

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    window.addEventListener('beforeinstallprompt', captureInstallPrompt);

    if ('serviceWorker' in navigator) {
      let reloadedForUpdate = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloadedForUpdate) return;
        reloadedForUpdate = true;
        window.location.reload();
      });

      navigator.serviceWorker
        .register('./service-worker.js', { scope: './' })
        .then((registration) => {
          setSwStatus('Registered');
          registration.update().catch(() => undefined);
        })
        .catch((error) => setSwStatus(`Registration failed: ${error.message}`));
    } else {
      setSwStatus('Not supported');
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (!canStoreUserData) return;
    const savedQueue = window.localStorage?.getItem('mindflow-offline-queue');
    if (savedQueue) {
      try {
        setOfflineQueue(JSON.parse(savedQueue));
      } catch (error) {
        setOfflineQueue([]);
      }
    }
  }, [canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-offline-queue', JSON.stringify(offlineQueue));
  }, [offlineQueue, canStoreUserData]);

  useEffect(() => {
    if (canStoreUserData && isOnline && offlineQueue.length > 0) {
      const syncedItems = offlineQueue.map((item) => ({ ...item, syncedAt: new Date().toISOString() }));
      window.localStorage?.setItem('mindflow-last-sync-preview', JSON.stringify(syncedItems));
      setOfflineQueue([]);
    }
  }, [isOnline, offlineQueue, canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    const savedUser = window.localStorage?.getItem('mindflow-current-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setCurrentUser(parsed);
        setProfileForm(parsed);
      } catch (error) {
        setCurrentUser(DEFAULT_USER);
        setProfileForm(DEFAULT_USER);
      }
    }
  }, [canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-current-user', JSON.stringify(currentUser));
    setProfileForm(currentUser);
  }, [currentUser, canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    const savedMeditations = window.localStorage?.getItem('mindflow-meditation-sessions');
    if (savedMeditations) {
      try {
        setMeditationSessions(JSON.parse(savedMeditations));
      } catch (error) {
        setMeditationSessions([]);
      }
    }

    const savedRoutines = window.localStorage?.getItem('mindflow-ai-routines');
    if (savedRoutines) {
      try {
        setRoutineSaved(JSON.parse(savedRoutines));
      } catch (error) {
        setRoutineSaved([]);
      }
    }
  }, [canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-meditation-sessions', JSON.stringify(meditationSessions));
  }, [meditationSessions, canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-ai-routines', JSON.stringify(routineSaved));
  }, [routineSaved, canStoreUserData]);

  useEffect(() => {
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.lowStim = lowStimulation ? 'true' : 'false';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', darkMode ? '#0f172a' : '#fafcff');
    if (canStoreUserData) window.localStorage?.setItem('mindflow-theme', theme);
  }, [darkMode, canStoreUserData, lowStimulation]);

  useFocusTrap(authOpen, authModalRef);
  useFocusTrap(panicModeOpen, panicModalRef);
  useFocusTrap(routineRunnerOpen, routineRunnerRef);

  useEffect(() => {
    if (!canStoreUserData) return;
    const saved = window.localStorage?.getItem(BREATH_SESSIONS_KEY);
    if (saved) {
      const parsed = Number(saved);
      if (!Number.isNaN(parsed)) setBreathSessionsCompleted(parsed);
    }
  }, [canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem(BREATH_SESSIONS_KEY, String(breathSessionsCompleted));
  }, [breathSessionsCompleted, canStoreUserData]);

  useEffect(() => {
    if (!completed) {
      lastBreathCompletedRef.current = false;
      return;
    }
    if (lastBreathCompletedRef.current) return;
    lastBreathCompletedRef.current = true;
    setBreathSessionsCompleted((count) => count + 1);
  }, [completed]);

  useEffect(() => {
    if (!isRunning || completed) return;
    const label = phase.label;
    setBreathPhaseAnnouncement(`${label}, ${secondsLeft} seconds remaining`);
  }, [phaseIndex, isRunning, completed, phase.label, secondsLeft]);

  useEffect(() => {
    if (!isRunning || completed) return;
    vibratePhase(hapticsEnabled);
  }, [phaseIndex, isRunning, completed, hapticsEnabled]);

  useEffect(() => {
    if (!routineRunnerRunning || !routineRunnerStep) return undefined;

    const timer = window.setInterval(() => {
      setRoutineRunnerSecondsLeft((current) => {
        if (current > 1) return current - 1;
        setRoutineRunnerRunning(false);
        return 0;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [routineRunnerRunning, routineRunnerStep]);

  useEffect(() => {
    setFocusRunning(false);
    setFocusMode('focus');
    setFocusRound(1);
    setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60);
    setFocusCompleted(0);
    setFocusTipIndex(0);
  }, [selectedFocusPreset]);

  useEffect(() => {
    if (!focusRunning) return undefined;

    const timer = window.setInterval(() => {
      setFocusSecondsLeft((current) => {
        if (current > 1) return current - 1;

        setFocusTipIndex((tip) => tip + 1);

        if (focusMode === 'focus') {
          setFocusCompleted((count) => count + 1);
          setFocusMode('break');
          setTimeout(() => setFocusSecondsLeft(selectedFocusPreset.breakMinutes * 60), 0);
          return current;
        }

        setFocusRound((currentRound) => {
          if (currentRound >= selectedFocusPreset.rounds) {
            setFocusRunning(false);
            setFocusMode('focus');
            setTimeout(() => setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60), 0);
            return currentRound;
          }

          setFocusMode('focus');
          setTimeout(() => setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60), 0);
          return currentRound + 1;
        });

        return current;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [focusRunning, focusMode, selectedFocusPreset]);

  useEffect(() => {
    if (!canStoreUserData) return;
    const saved = window.localStorage?.getItem('mindflow-cbt-entries');
    if (saved) {
      try {
        setEntries(JSON.parse(saved));
      } catch (error) {
        setEntries([]);
      }
    }

    const savedMood = window.localStorage?.getItem('mindflow-mood-entries');
    if (savedMood) {
      try {
        setMoodEntries(JSON.parse(savedMood));
      } catch (error) {
        setMoodEntries([]);
      }
    }
  }, [canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-cbt-entries', JSON.stringify(entries));
  }, [entries, canStoreUserData]);

  useEffect(() => {
    if (!canStoreUserData) return;
    window.localStorage?.setItem('mindflow-mood-entries', JSON.stringify(moodEntries));
  }, [moodEntries, canStoreUserData]);

  useEffect(() => {
    if (!isRunning || completed) return undefined;

    const timer = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current > 1) return current - 1;

        setPhaseIndex((currentPhase) => {
          const nextPhase = currentPhase + 1;

          if (nextPhase < selected.pattern.length) {
            setTimeout(() => setSecondsLeft(selected.pattern[nextPhase].seconds), 0);
            return nextPhase;
          }

          setRound((currentRound) => {
            if (currentRound >= selected.rounds) {
              setIsRunning(false);
              setCompleted(true);
              setTimeout(() => setSecondsLeft(0), 0);
              return currentRound;
            }

            setTimeout(() => {
              setPhaseIndex(0);
              setSecondsLeft(selected.pattern[0].seconds);
            }, 0);

            return currentRound + 1;
          });

          return currentPhase;
        });

        return current;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRunning, completed, selected]);

  useEffect(() => {
    if (!isRunning || completed) return;

    const cueKey = `${phaseIndex}-${phase.label}`;
    if (cueKey === lastBreathToneRef.current) return;

    lastBreathToneRef.current = cueKey;
    playSoftTone(breathPhaseFrequency(phase.label), 0.42, 0.13, ensureAudioContext());
  }, [phaseIndex, isRunning, phase.label, completed]);

  useEffect(() => {
    if (!completed) return;
    playSoftTone(528, 0.9, 0.1, ensureAudioContext());
    window.setTimeout(() => playSoftTone(396, 0.9, 0.08, ensureAudioContext()), 220);
  }, [completed]);

  function startSession() {
    setCompleted(false);
    if (secondsLeft === 0) {
      setPhaseIndex(0);
      setRound(1);
      setSecondsLeft(selected.pattern[0].seconds);
    }
    lastBreathToneRef.current = '';
    setIsRunning(true);
  }

  function pauseSession() {
    setIsRunning(false);
    if (!meditationRunning) stopAmbience();
  }

  function resetSession() {
    setIsRunning(false);
    setCompleted(false);
    setPhaseIndex(0);
    setRound(1);
    setSecondsLeft(selected.pattern[0].seconds);
    lastBreathToneRef.current = '';
    if (!meditationRunning) stopAmbience();
  }

  function selectTechnique(id) {
    setSelectedId(id);
  }

  function updateJournal(key, value) {
    setJournal((current) => ({ ...current, [key]: value }));
  }

  function toggleJournalArray(key, value) {
    setJournal((current) => {
      const currentValues = current[key];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];
      return { ...current, [key]: nextValues };
    });
  }

  function saveJournalEntry() {
    const timestamp = new Date().toISOString();
    const entry = {
      ...journal,
      id: activeEntryId || crypto.randomUUID(),
      createdAt: activeEntryId
        ? entries.find((item) => item.id === activeEntryId)?.createdAt || timestamp
        : timestamp,
      updatedAt: timestamp
    };

    setEntries((current) => {
      const exists = current.some((item) => item.id === entry.id);
      if (exists) return current.map((item) => (item.id === entry.id ? entry : item));
      return [entry, ...current];
    });

    if (!isOnline) {
      queueOfflineAction('cbt-entry', entry);
    }

    setActiveEntryId(entry.id);
  }

  function newJournalEntry() {
    setJournal(emptyJournal());
    setActiveEntryId(null);
  }

  function loadJournalEntry(entry) {
    setJournal({
      situation: entry.situation || '',
      automaticThought: entry.automaticThought || '',
      emotions: entry.emotions || [],
      intensityBefore: entry.intensityBefore || 70,
      evidenceFor: entry.evidenceFor || '',
      evidenceAgainst: entry.evidenceAgainst || '',
      distortions: entry.distortions || [],
      balancedThought: entry.balancedThought || '',
      actionStep: entry.actionStep || '',
      intensityAfter: entry.intensityAfter || 35
    });
    setActiveEntryId(entry.id);
  }

  function deleteJournalEntry(id) {
    setEntries((current) => current.filter((item) => item.id !== id));
    if (activeEntryId === id) newJournalEntry();
  }

  function openPanicMode() {
    setPanicModeOpen(true);
    setPanicStep(0);
    setPanicIntensity(85);
    setPanicStatementIndex(0);
    setSelectedId('sigh');
    resetSession();
  }

  function closePanicMode() {
    setPanicModeOpen(false);
  }

  function nextPanicStep() {
    setPanicStep((current) => Math.min(PANIC_STEPS.length - 1, current + 1));
    setPanicStatementIndex((current) => (current + 1) % SAFETY_STATEMENTS.length);
  }

  function previousPanicStep() {
    setPanicStep((current) => Math.max(0, current - 1));
  }

  function updatePanicNote(stepIndex, value) {
    setPanicNotes((current) => ({ ...current, [stepIndex]: value }));
  }

  function startFocusTimer() {
    setFocusRunning(true);
  }

  function pauseFocusTimer() {
    setFocusRunning(false);
  }

  function resetFocusTimer() {
    setFocusRunning(false);
    setFocusMode('focus');
    setFocusRound(1);
    setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60);
    setFocusCompleted(0);
    setFocusTipIndex(0);
  }

  function skipFocusPhase() {
    if (focusMode === 'focus') {
      setFocusCompleted((count) => count + 1);
      setFocusMode('break');
      setFocusSecondsLeft(selectedFocusPreset.breakMinutes * 60);
      setFocusTipIndex((tip) => tip + 1);
    } else {
      if (focusRound >= selectedFocusPreset.rounds) {
        setFocusRunning(false);
        setFocusMode('focus');
        setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60);
      } else {
        setFocusRound((current) => current + 1);
        setFocusMode('focus');
        setFocusSecondsLeft(selectedFocusPreset.focusMinutes * 60);
      }
    }
  }

  function addDistraction() {
    const text = focusDistraction.trim();
    if (!text) return;
    setFocusDistractions((current) => [
      { id: crypto.randomUUID(), text, done: false },
      ...current
    ]);
    setFocusDistraction('');
  }

  function toggleDistraction(id) {
    setFocusDistractions((current) => current.map((item) => item.id === id ? { ...item, done: !item.done } : item));
  }

  function removeDistraction(id) {
    setFocusDistractions((current) => current.filter((item) => item.id !== id));
  }

  function updateMoodCheckIn(key, value) {
    setMoodCheckIn((current) => ({ ...current, [key]: value }));
  }

  function toggleMoodTag(tag) {
    setMoodCheckIn((current) => ({
      ...current,
      tags: current.tags.includes(tag)
        ? current.tags.filter((item) => item !== tag)
        : [...current.tags, tag]
    }));
  }

  function saveMoodCheckIn() {
    const timestamp = new Date().toISOString();
    const entry = {
      ...moodCheckIn,
      id: activeMoodId || crypto.randomUUID(),
      createdAt: activeMoodId
        ? moodEntries.find((item) => item.id === activeMoodId)?.createdAt || timestamp
        : timestamp,
      updatedAt: timestamp
    };

    setMoodEntries((current) => {
      const exists = current.some((item) => item.id === entry.id);
      if (exists) return current.map((item) => (item.id === entry.id ? entry : item));
      return [entry, ...current];
    });

    if (!isOnline) {
      queueOfflineAction('mood-check-in', entry);
    }

    setActiveMoodId(entry.id);
  }

  function newMoodCheckIn() {
    setMoodCheckIn(emptyMoodCheckIn());
    setActiveMoodId(null);
  }

  function loadMoodEntry(entry) {
    setMoodCheckIn({
      mood: entry.mood ?? 55,
      anxiety: entry.anxiety ?? 45,
      focus: entry.focus ?? 50,
      energy: entry.energy ?? 50,
      sleep: entry.sleep ?? 50,
      stress: entry.stress ?? 45,
      tags: entry.tags || [],
      note: entry.note || '',
      win: entry.win || '',
      support: entry.support || ''
    });
    setActiveMoodId(entry.id);
  }

  function deleteMoodEntry(id) {
    setMoodEntries((current) => current.filter((item) => item.id !== id));
    if (activeMoodId === id) newMoodCheckIn();
  }

  function ensureAudioContext() {
    if (!audioRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      audioRef.current = new AudioContextClass();
    }
    return audioRef.current;
  }

  function clearAmbienceNodes() {
    if (ambienceCrackleRef.current) {
      window.clearInterval(ambienceCrackleRef.current);
      ambienceCrackleRef.current = null;
    }

    if (ambienceAudioRef.current) {
      try {
        ambienceAudioRef.current.pause();
        ambienceAudioRef.current.removeAttribute('src');
        ambienceAudioRef.current.load();
      } catch (error) {
        // Already stopped.
      }
      ambienceAudioRef.current = null;
    }

    ambienceNodesRef.current.forEach((node) => {
      try {
        if (typeof node.stop === 'function') node.stop();
        node.disconnect();
      } catch (error) {
        // Already stopped.
      }
    });
    ambienceNodesRef.current = [];
    ambienceMasterGainRef.current = null;
    activeAmbienceIdRef.current = null;
  }

  function trackAmbienceNode(node) {
    if (node) ambienceNodesRef.current.push(node);
  }

  function setAmbienceVolume(volumePercent) {
    const nextVolume = Math.min(1, Math.max(0, volumePercent / 100));
    if (ambienceAudioRef.current) {
      ambienceAudioRef.current.volume = nextVolume;
    }
    if (ambienceMasterGainRef.current) {
      ambienceMasterGainRef.current.gain.value = nextVolume * 0.28;
    }
  }

  function createNoiseBuffer(context, colour) {
    const seconds = 2;
    const bufferSize = Math.floor(context.sampleRate * seconds);
    const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
    const data = buffer.getChannelData(0);
    let lastOut = 0;

    for (let index = 0; index < bufferSize; index += 1) {
      const white = Math.random() * 2 - 1;
      if (colour === 'brown') {
        lastOut = (lastOut + 0.02 * white) / 1.02;
        data[index] = lastOut * 3.2;
      } else if (colour === 'pink') {
        lastOut = 0.98 * lastOut + 0.02 * white;
        data[index] = lastOut * 2.2;
      } else {
        data[index] = white * 0.35;
      }
    }

    return buffer;
  }

  function addAmbiencePad(context, masterGain, frequency, volume = 0.035, wave = 'sine') {
    const pad = context.createOscillator();
    const padGain = context.createGain();
    pad.type = wave;
    pad.frequency.value = frequency;
    padGain.gain.value = volume;
    pad.connect(padGain);
    padGain.connect(masterGain);
    pad.start();
    trackAmbienceNode(pad);
    trackAmbienceNode(padGain);
  }

  function addAmbienceNoise(context, masterGain, colour, filterSettings, gain = 1) {
    const noise = context.createBufferSource();
    noise.buffer = createNoiseBuffer(context, colour);
    noise.loop = true;

    const filter = context.createBiquadFilter();
    filter.type = filterSettings.type || 'lowpass';
    filter.frequency.value = filterSettings.frequency || 500;
    if (filterSettings.Q != null) filter.Q.value = filterSettings.Q;

    const layerGain = context.createGain();
    layerGain.gain.value = gain;
    noise.connect(filter);
    filter.connect(layerGain);
    layerGain.connect(masterGain);
    noise.start();
    trackAmbienceNode(noise);
    trackAmbienceNode(filter);
    trackAmbienceNode(layerGain);
    return { filter, layerGain };
  }

  function addFilterSwell(context, filter, rate, depth) {
    const swell = context.createOscillator();
    const swellGain = context.createGain();
    swell.type = 'sine';
    swell.frequency.value = rate;
    swellGain.gain.value = depth;
    swell.connect(swellGain);
    swellGain.connect(filter.frequency);
    swell.start();
    trackAmbienceNode(swell);
    trackAmbienceNode(swellGain);
  }

  function addGainSwell(context, targetGain, rate, depth) {
    const swell = context.createOscillator();
    const swellGain = context.createGain();
    swell.type = 'sine';
    swell.frequency.value = rate;
    swellGain.gain.value = depth;
    swell.connect(swellGain);
    swellGain.connect(targetGain.gain);
    swell.start();
    trackAmbienceNode(swell);
    trackAmbienceNode(swellGain);
  }

  function addCrackleBursts(context, masterGain) {
    ambienceCrackleRef.current = window.setInterval(() => {
      try {
        const burst = context.createBufferSource();
        const bufferSize = Math.floor(context.sampleRate * 0.06);
        const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
        const data = buffer.getChannelData(0);
        for (let index = 0; index < bufferSize; index += 1) {
          data[index] = (Math.random() * 2 - 1) * (1 - index / bufferSize);
        }
        burst.buffer = buffer;
        const gain = context.createGain();
        gain.gain.value = 0.08 + Math.random() * 0.06;
        burst.connect(gain);
        gain.connect(masterGain);
        const now = context.currentTime;
        burst.start(now);
        burst.stop(now + 0.07);
      } catch (error) {
        // Optional texture.
      }
    }, 900 + Math.random() * 1400);
  }

  function startSampledAmbience(preset, volumePercent) {
    const audio = new Audio(preset.file);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = Math.min(1, Math.max(0, volumePercent / 100));
    ambienceAudioRef.current = audio;
    audio.play().catch(() => undefined);
  }

  function startTranquilAmbience(ambienceId, volumePercent = 35) {
    try {
      if (!ambienceId || ambienceId === 'none') {
        stopAmbience();
        return;
      }

      const preset = AMBIENCE_OPTIONS.find((item) => item.id === ambienceId) || AMBIENCE_OPTIONS[0];
      if (activeAmbienceIdRef.current === ambienceId && ambienceMasterGainRef.current) {
        setAmbienceVolume(volumePercent);
        return;
      }
      if (activeAmbienceIdRef.current === ambienceId && ambienceAudioRef.current) {
        setAmbienceVolume(volumePercent);
        return;
      }

      stopAmbience();
      activeAmbienceIdRef.current = ambienceId;

      if (preset.file) {
        startSampledAmbience(preset, volumePercent);
        return;
      }

      const context = ensureAudioContext();
      const masterGain = context.createGain();
      masterGain.gain.value = (volumePercent / 100) * 0.28;
      masterGain.connect(context.destination);
      ambienceMasterGainRef.current = masterGain;
      trackAmbienceNode(masterGain);

      if (ambienceId === 'zen') {
        addAmbiencePad(context, masterGain, preset.frequency, 0.05);
        addAmbiencePad(context, masterGain, preset.frequency * 1.5, 0.018);
        return;
      }

      if (ambienceId === 'healing') {
        addAmbiencePad(context, masterGain, 528, 0.045);
        addAmbiencePad(context, masterGain, 264, 0.022);
        addAmbiencePad(context, masterGain, 396, 0.012);
        return;
      }

      if (ambienceId === 'forest') {
        const low = addAmbienceNoise(context, masterGain, 'brown', { type: 'lowpass', frequency: 280 }, 0.7);
        const high = addAmbienceNoise(context, masterGain, 'white', { type: 'bandpass', frequency: 2800, Q: 0.5 }, 0.18);
        addFilterSwell(context, high.filter, 0.15, 900);
        addAmbiencePad(context, masterGain, preset.frequency, 0.02);
        return;
      }

      if (ambienceId === 'wind') {
        const layer = addAmbienceNoise(context, masterGain, 'white', { type: 'highpass', frequency: 380 }, 0.55);
        addGainSwell(context, layer.layerGain, 0.05, 0.12);
        addAmbiencePad(context, masterGain, preset.frequency, 0.015);
        return;
      }

      if (ambienceId === 'fireplace') {
        const hearth = addAmbienceNoise(context, masterGain, 'brown', { type: 'bandpass', frequency: 520, Q: 0.6 }, 0.85);
        addFilterSwell(context, hearth.filter, 0.09, 120);
        addAmbiencePad(context, masterGain, preset.frequency, 0.028);
        addCrackleBursts(context, masterGain);
        return;
      }

      if (ambienceId === 'night') {
        addAmbienceNoise(context, masterGain, 'brown', { type: 'lowpass', frequency: 140 }, 0.5);
        addAmbiencePad(context, masterGain, preset.frequency, 0.03);
        addGainSwell(context, masterGain, 0.03, 0.04);
        return;
      }

      if (ambienceId === 'stream') {
        const stream = addAmbienceNoise(context, masterGain, 'white', { type: 'bandpass', frequency: 1400, Q: 0.45 }, 0.65);
        addFilterSwell(context, stream.filter, 0.22, 500);
        addAmbiencePad(context, masterGain, preset.frequency, 0.025);
        return;
      }

      if (ambienceId === 'thunder') {
        const storm = addAmbienceNoise(context, masterGain, 'white', { type: 'bandpass', frequency: 680, Q: 0.35 }, 0.75);
        addFilterSwell(context, storm.filter, 0.1, 110);
        addAmbiencePad(context, masterGain, preset.frequency, 0.03);
        ambienceCrackleRef.current = window.setInterval(() => {
          playSoftTone(72 + Math.random() * 24, 1.8, 0.04, context);
        }, 14000 + Math.random() * 12000);
        return;
      }

      const noiseColour =
        ambienceId === 'brown' ? 'brown' : ambienceId === 'pink' ? 'pink' : 'white';

      let filterSettings = { type: 'lowpass', frequency: 560 };
      if (ambienceId === 'rain') filterSettings = { type: 'bandpass', frequency: 720, Q: 0.35 };
      if (ambienceId === 'ocean') filterSettings = { type: 'lowpass', frequency: 420 };
      if (ambienceId === 'brown') filterSettings = { type: 'lowpass', frequency: 180 };
      if (ambienceId === 'white') filterSettings = { type: 'lowpass', frequency: 900 };

      const layer = addAmbienceNoise(context, masterGain, noiseColour, filterSettings, 1);

      if (ambienceId === 'ocean' || ambienceId === 'rain') {
        addFilterSwell(context, layer.filter, ambienceId === 'ocean' ? 0.07 : 0.12, ambienceId === 'ocean' ? 140 : 90);
      }

      if (ambienceId !== 'white' && ambienceId !== 'brown' && ambienceId !== 'pink') {
        addAmbiencePad(context, masterGain, preset.frequency, 0.035);
      } else {
        addAmbiencePad(context, masterGain, preset.frequency, 0.02);
      }
    } catch (error) {
      // Ambient audio is optional and can be blocked by browser permissions.
    }
  }

  function selectSoundTrack(trackId) {
    setAmbienceTrackId(trackId);
    if (trackId === 'none') {
      setSoundLibraryPlaying(false);
      stopAmbience();
    }
  }

  function toggleSoundLibrary() {
    if (ambienceTrackId === 'none') return;
    setSoundLibraryPlaying((current) => !current);
  }

  function previewSoundTrack(trackId) {
    ensureAudioContext();
    selectSoundTrack(trackId);
    setSoundLibraryPlaying(true);
  }

  function stopAmbience() {
    clearAmbienceNodes();
  }

  function playMeditationBell() {
    try {
      const context = ensureAudioContext();
      [528, 396].forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        oscillator.connect(gain);
        gain.connect(context.destination);
        const start = context.currentTime + index * 0.18;
        gain.gain.setValueAtTime(0.15, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 1.4);
        oscillator.start(start);
        oscillator.stop(start + 1.4);
      });
    } catch (error) {
      // Bell audio is optional.
    }
  }

  function startMeditation() {
    if (meditationSecondsLeft === 0) {
      setMeditationSecondsLeft(selectedMeditation.minutes * 60);
      setMeditationScriptIndex(0);
    }
    playMeditationBell();
    setMeditationRunning(true);
  }

  function pauseMeditation() {
    setMeditationRunning(false);
    if (!isRunning) stopAmbience();
  }

  function resetMeditation() {
    setMeditationRunning(false);
    setMeditationSecondsLeft(selectedMeditation.minutes * 60);
    setMeditationScriptIndex(0);
    stopAmbience();
  }

  function completeMeditationSession() {
    setMeditationRunning(false);
    stopAmbience();
    playMeditationBell();
    setMeditationSessions((current) => [
      {
        id: crypto.randomUUID(),
        meditationId: selectedMeditation.id,
        name: selectedMeditation.name,
        minutes: selectedMeditation.minutes,
        ambience: selectedMeditation.ambience,
        completedAt: new Date().toISOString()
      },
      ...current
    ]);
  }

  function deleteMeditationSession(id) {
    setMeditationSessions((current) => current.filter((item) => item.id !== id));
  }

  function navigateToTool(toolId) {
    setMainNav('tools');
    setActiveTool(toolId);
    window.scrollTo({ top: 0, behavior: calmMotion ? 'smooth' : 'auto' });
  }

  function handleNeedSelection(needId) {
    if (needId === 'panic') {
      openPanicMode();
      return;
    }
    if (needId === 'journal') {
      setMainNav('journal');
      window.scrollTo({ top: 0, behavior: calmMotion ? 'smooth' : 'auto' });
      return;
    }
    if (needId === 'breathe') {
      navigateToTool('breathe');
      return;
    }
    if (needId === 'focus') {
      navigateToTool('focus');
    }
  }

  function exitImmersiveSession() {
    if (activeTool === 'breathe' && isRunning) pauseSession();
    if (activeTool === 'meditate' && meditationRunning) pauseMeditation();
    if (activeTool === 'focus' && focusRunning) pauseFocusTimer();
  }

  function applyBalancedSuggestion() {
    const suggestion = suggestBalancedThought(journal);
    updateJournal('balancedThought', suggestion);
    setReframeMessage('Balanced thought suggested — edit it until it feels honest.');
    window.setTimeout(() => setReframeMessage(''), 4000);
  }

  function startRoutineRunner(steps) {
    if (!steps.length) return;
    setRoutineRunnerSteps(steps);
    setRoutineRunnerStepIndex(0);
    setRoutineRunnerSecondsLeft(steps[0].minutes * 60);
    setRoutineRunnerRunning(false);
    setRoutineRunnerOpen(true);
  }

  function closeRoutineRunner() {
    setRoutineRunnerOpen(false);
    setRoutineRunnerRunning(false);
    setRoutineRunnerSteps([]);
    setRoutineRunnerStepIndex(0);
    setRoutineRunnerSecondsLeft(0);
  }

  function startRoutineRunnerTimer() {
    if (!routineRunnerStep) return;
    if (routineRunnerSecondsLeft === 0) {
      setRoutineRunnerSecondsLeft(routineRunnerStep.minutes * 60);
    }
    setRoutineRunnerRunning(true);
  }

  function pauseRoutineRunnerTimer() {
    setRoutineRunnerRunning(false);
  }

  function nextRoutineRunnerStep() {
    if (routineRunnerStepIndex >= routineRunnerSteps.length - 1) {
      closeRoutineRunner();
      return;
    }
    const nextIndex = routineRunnerStepIndex + 1;
    setRoutineRunnerStepIndex(nextIndex);
    setRoutineRunnerSecondsLeft(routineRunnerSteps[nextIndex].minutes * 60);
    setRoutineRunnerRunning(false);
  }

  function previousRoutineRunnerStep() {
    if (routineRunnerStepIndex === 0) return;
    const prevIndex = routineRunnerStepIndex - 1;
    setRoutineRunnerStepIndex(prevIndex);
    setRoutineRunnerSecondsLeft(routineRunnerSteps[prevIndex].minutes * 60);
    setRoutineRunnerRunning(false);
  }

  function saveGeneratedRoutine() {
    setRoutineSaved((current) => [
      {
        id: crypto.randomUUID(),
        state: routineStateLabel,
        goal: routineGoal,
        intensity: routineIntensity,
        minutes: routineTotalMinutes,
        supportStyle: routineSupportStyle,
        steps: generatedRoutine,
        createdAt: new Date().toISOString()
      },
      ...current
    ]);
  }

  function deleteSavedRoutine(id) {
    setRoutineSaved((current) => current.filter((item) => item.id !== id));
  }

  function openRoutineTool(step) {
    const tool = `${step.tool} ${step.type}`.toLowerCase();
    if (tool.includes('panic') || tool.includes('grounding') || tool.includes('safety')) {
      openPanicMode();
      return;
    }
    if (tool.includes('breath') || tool.includes('sigh') || tool.includes('exhale') || tool.includes('box') || tool.includes('4-7-8')) {
      if (tool.includes('sigh')) setSelectedId('sigh');
      else if (tool.includes('4-7-8')) setSelectedId('478');
      else if (tool.includes('exhale')) setSelectedId('extended');
      else setSelectedId('box');
      navigateToTool('breathe');
      return;
    }
    if (tool.includes('meditation') || tool.includes('sleep') || tool.includes('body scan')) {
      if (tool.includes('sleep')) setMeditationId('sleep-wind-down');
      else if (tool.includes('body')) setMeditationId('body-scan');
      else if (tool.includes('adhd')) setMeditationId('adhd-focus');
      else setMeditationId('anxiety-calm');
      navigateToTool('meditate');
      return;
    }
    if (tool.includes('journal') || tool.includes('cbt') || tool.includes('thought')) {
      setMainNav('journal');
      return;
    }
    if (tool.includes('timer') || tool.includes('focus') || tool.includes('adhd')) {
      setFocusPresetId(tool.includes('low') ? 'low-energy' : 'quick-start');
      navigateToTool('focus');
    }
  }

  function queueOfflineAction(type, payload) {
    if (!canStoreUserData) return;
    setOfflineQueue((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        type,
        payload,
        createdAt: new Date().toISOString()
      }
    ]);
  }

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
    setIsInstallable(false);
  }

  function updateAuthForm(key, value) {
    setAuthForm((current) => ({ ...current, [key]: value }));
  }

  function openAuth(mode = 'signin') {
    setAuthMode(mode);
    setAuthOpen(true);
    setAuthMessage('');
  }

  function closeAuth() {
    setAuthOpen(false);
    setAuthMessage('');
  }

  function submitAuth(event) {
    event.preventDefault();
    if (!authForm.email.trim()) {
      setAuthMessage('Please enter an email address.');
      return;
    }
    if (!authForm.password.trim()) {
      setAuthMessage('Please enter a password.');
      return;
    }

    const user = {
      ...DEFAULT_USER,
      id: `local-${authForm.email.trim().toLowerCase()}`,
      name: authMode === 'signup' ? authForm.name.trim() || 'Mind Wanders User' : authForm.email.split('@')[0],
      email: authForm.email.trim().toLowerCase(),
      plan: 'Free',
      avatar: authMode === 'signup' ? '🌱' : '🌿',
      createdAt: new Date().toISOString()
    };

    setCurrentUser(user);
    setProfileForm(user);
    setAuthForm({ name: '', email: '', password: '' });
    setAuthMessage(authMode === 'signup' ? 'Account created locally.' : 'Signed in locally.');
    setTimeout(() => closeAuth(), 600);
  }

  function continueAsGuest() {
    setCurrentUser(DEFAULT_USER);
    setProfileForm(DEFAULT_USER);
    closeAuth();
  }

  function signOut() {
    setCurrentUser(DEFAULT_USER);
    setProfileForm(DEFAULT_USER);
    window.localStorage?.removeItem('mindflow-current-user');
    setAuthMessage('Signed out.');
  }

  function updateProfilePreference(key, value) {
    setProfileForm((current) => ({
      ...current,
      preferences: {
        ...current.preferences,
        [key]: value
      }
    }));
  }

  function updateProfileField(key, value) {
    setProfileForm((current) => ({ ...current, [key]: value }));
  }

  function saveProfile() {
    setCurrentUser(profileForm);
    setAuthMessage('Profile saved.');
    setTimeout(() => setAuthMessage(''), 1200);
  }

  function resetStoredState() {
    setEntries([]);
    setMoodEntries([]);
    setMeditationSessions([]);
    setRoutineSaved([]);
    setOfflineQueue([]);
    setCurrentUser(DEFAULT_USER);
    setProfileForm(DEFAULT_USER);
    setActiveMoodId(null);
    setAuthMessage('');
  }

  async function clearStoredInformation({ preserveConsent = false } = {}) {
    LOCAL_STORAGE_KEYS.forEach((key) => window.localStorage?.removeItem(key));
    if (!preserveConsent) window.localStorage?.removeItem(STORAGE_CONSENT_KEY);

    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(
        cacheKeys
          .filter((key) => key.startsWith('mindflow-'))
          .map((key) => caches.delete(key))
      );
    }

    resetStoredState();
  }

  function acceptDataStorage() {
    window.localStorage?.setItem(STORAGE_CONSENT_KEY, 'accepted');
    setStorageConsent('accepted');
    setPrivacyMessage('Storage accepted. Your app data can be saved on this device.');
  }

  function rejectDataStorage() {
    window.localStorage?.setItem(STORAGE_CONSENT_KEY, 'rejected');
    setStorageConsent('rejected');
    clearStoredInformation({ preserveConsent: true }).then(() => {
      window.localStorage?.setItem(STORAGE_CONSENT_KEY, 'rejected');
      setPrivacyMessage('Optional storage rejected. Existing local app data was removed.');
    });
  }

  function wipeAllStoredInformation() {
    const confirmed = window.confirm('Delete all Mind Wanders data stored in this browser, including local journals, mood entries, profile details, preferences, offline queue and app caches?');
    if (!confirmed) return;

    setStorageConsent('pending');
    clearStoredInformation().then(() => {
      setDarkMode(false);
      document.documentElement.dataset.theme = 'light';
      setPrivacyMessage('All Mind Wanders data stored in this browser has been wiped.');
    });
  }

  return (
    <div
      className={`min-h-screen text-slate-700 overflow-x-hidden ${darkMode ? 'dark-mode' : ''}`}
      style={{ background: 'var(--surface-base)', color: 'var(--text-primary)' }}
    >
      {!lowStimulation && (
        <>
          <div className="ambient-gradient fixed inset-0 pointer-events-none bg-gradient-to-br from-teal-100/70 via-violet-50/80 to-sky-100/70" />
          <div className="ambient-radial fixed inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_0%,rgba(153,246,228,0.35),transparent_50%),radial-gradient(circle_at_90%_90%,rgba(221,214,254,0.35),transparent_45%)]" />
        </>
      )}

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {breathPhaseAnnouncement}
      </div>

      {storageConsent === 'pending' && (
        <section className="fixed inset-x-4 privacy-consent-panel z-[70] max-w-5xl mx-auto rounded-[28px] border border-slate-200 bg-white/95 shadow-2xl p-5 md:p-6">
          <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <div className="text-sm uppercase tracking-[0.25em] text-teal-600 mb-2">Privacy & storage</div>
              <h2 className="text-2xl font-black mb-2">Choose how Mind Wanders stores data</h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                We do not use advertising or tracking cookies. If you accept, journals, mood entries, profile preferences,
                theme choice, meditation history, routines and offline data are stored in this browser with localStorage and cache.
                If you reject, the app works for the current session but will not save optional personal data on this device.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2 min-w-52">
              <button type="button" onClick={acceptDataStorage} className="rounded-2xl bg-teal-300 px-5 py-3 text-teal-950 font-black">
                Accept cookies/storage
              </button>
              <button type="button" onClick={rejectDataStorage} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-slate-600 font-bold hover:bg-violet-100">
                Reject optional cookies/storage
              </button>
              <button type="button" onClick={() => setMainNav('you')} className="text-sm text-slate-500 hover:text-teal-700">
                View details in You
              </button>
            </div>
          </div>
        </section>
      )}

      <main className={`relative z-10 max-w-7xl mx-auto px-5 py-6 sm:py-10 lg:py-16 ${!isImmersive ? 'bottom-nav-spacer' : ''}`}>
        {authOpen && (
          <section className={`${overlayPanelClass} z-[60] bg-[#fafcff]/90 backdrop-blur-xl`}>
            <div ref={authModalRef} className="min-h-full max-w-5xl mx-auto px-5 py-8 flex items-center justify-center">
              <div className="w-full rounded-[36px] border border-slate-200 bg-white/95 p-6 md:p-8 shadow-2xl">
                <div className="flex items-start justify-between gap-4 mb-8">
                  <div>
                    <div className="text-sm uppercase tracking-[0.3em] text-teal-600/80 mb-2">Account</div>
                    <h2 className="text-4xl font-black">{authMode === 'signup' ? 'Create your Mind Wanders account' : 'Sign in to Mind Wanders'}</h2>
                    <p className="text-slate-500 mt-3 max-w-2xl">This demo uses local browser storage. Connect Firebase or Supabase later for real authentication, cloud sync and subscriptions.</p>
                  </div>
                  <button onClick={closeAuth} className="rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 text-slate-600 hover:bg-violet-100">Close</button>
                </div>

                <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                  <form onSubmit={submitAuth} className="rounded-[28px] border border-slate-200 bg-white/85 p-6 space-y-5">
                    {authMode === 'signup' && (
                      <label className="block">
                        <div className="text-sm text-slate-500 mb-2">Name</div>
                        <input value={authForm.name} onChange={(event) => updateAuthForm('name', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" placeholder="Your name" />
                      </label>
                    )}
                    <label className="block">
                      <div className="text-sm text-slate-500 mb-2">Email</div>
                      <input type="email" value={authForm.email} onChange={(event) => updateAuthForm('email', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" placeholder="you@example.com" />
                    </label>
                    <label className="block">
                      <div className="text-sm text-slate-500 mb-2">Password</div>
                      <input type="password" value={authForm.password} onChange={(event) => updateAuthForm('password', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" placeholder="Password" />
                    </label>
                    {authMessage && <div className="rounded-2xl border border-teal-300 bg-teal-100 p-4 text-teal-800">{authMessage}</div>}
                    <button className="w-full rounded-2xl bg-teal-300 text-teal-900 font-black py-4">{authMode === 'signup' ? 'Create Account' : 'Sign In'}</button>
                    <button type="button" onClick={() => setAuthMode(authMode === 'signup' ? 'signin' : 'signup')} className="w-full rounded-2xl border border-slate-200 bg-white/90 py-4 text-slate-600 hover:bg-violet-100">
                      {authMode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Create one'}
                    </button>
                    <button type="button" onClick={continueAsGuest} className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-4 text-slate-500 hover:bg-violet-100">Continue as guest</button>
                  </form>

                  <aside className="rounded-[28px] border border-slate-200 bg-white/85 p-6">
                    <h3 className="text-2xl font-black mb-4">Account features</h3>
                    <div className="space-y-3 text-slate-600 leading-relaxed">
                      <p>Save CBT journals.</p>
                      <p>Save mood check-ins.</p>
                      <p>Track meditation sessions.</p>
                      <p>Save AI-generated routines.</p>
                      <p>Prepare for paid premium plans.</p>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
          </section>
        )}
        {!isImmersive && (
        <header className="mb-6 sticky top-0 z-40 -mx-5 px-5 py-3 backdrop-blur-xl border-b border-slate-200" style={{ background: 'var(--surface-header)' }}>
          <div className="flex flex-col gap-3 max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <img
                  src="logo.png"
                  alt="Mind Wanders — exploring pathways to mental wellness"
                  className="h-10 sm:h-11 w-auto max-w-[min(100%,220px)] sm:max-w-none shrink object-contain object-left"
                />
                <div className="text-xs text-slate-500 truncate hidden md:block">
                  {currentUser.name} · {isOnline ? 'Online' : 'Offline'}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setDarkMode((current) => !current)}
                  className="rounded-xl border border-slate-200 bg-white/90 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-violet-100"
                  aria-pressed={darkMode}
                >
                  {darkMode ? 'Light' : 'Dark'}
                </button>
                <button
                  type="button"
                  onClick={openPanicMode}
                  className="rounded-xl bg-rose-100 border border-rose-200 px-4 py-2 text-sm font-bold text-rose-700 hover:bg-rose-200"
                >
                  Panic help
                </button>
              </div>
            </div>
            {mainNav === 'tools' && (
              <nav className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Wellness tools">
                {TOOL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTool === tab.id}
                    onClick={() => setActiveTool(tab.id)}
                    className={`shrink-0 rounded-xl px-3.5 py-2 text-sm font-bold transition whitespace-nowrap ${
                      activeTool === tab.id
                        ? 'bg-teal-300 text-teal-900'
                        : 'border border-slate-200 bg-white/90 text-slate-600 hover:bg-violet-100'
                    }`}
                  >
                    <span className="mr-1" aria-hidden="true">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            )}
          </div>
        </header>
        )}

        {isImmersive && (
          <div className="fixed inset-x-0 app-session-topbar z-50 flex items-center justify-between gap-3 px-5 py-4">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">Session mode</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={exitImmersiveSession}
                className="rounded-xl border border-slate-200 bg-white/95 px-4 py-2 text-sm font-bold text-slate-600"
              >
                Pause
              </button>
              <button
                type="button"
                onClick={() => {
                  exitImmersiveSession();
                  setMainNav('now');
                }}
                className="rounded-xl bg-teal-300 px-4 py-2 text-sm font-black text-teal-900"
              >
                Exit
              </button>
            </div>
          </div>
        )}

        {mainNav === 'now' && (
          <section className="mb-12">
            <div className="mb-6 sm:mb-10">
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2 sm:mb-3">Right now</div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-black leading-tight mb-3 sm:mb-4">What do you need?</h1>
              <p className="text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
                Pick one path. You can always switch tools later — no account required.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10">
              {NEED_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleNeedSelection(option.id)}
                  className={`rounded-[28px] border p-6 text-left transition hover:-translate-y-0.5 motion-soft ${
                    option.accent === 'rose'
                      ? 'border-rose-200 bg-rose-50 hover:bg-rose-100'
                      : option.accent === 'teal'
                        ? 'border-teal-200 bg-teal-50 hover:bg-teal-100'
                        : option.accent === 'amber'
                          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                          : 'border-violet-200 bg-violet-50 hover:bg-violet-100'
                  }`}
                >
                  <div className="text-3xl mb-3" aria-hidden="true">{option.emoji}</div>
                  <div className="text-2xl font-black mb-1">{option.label}</div>
                  <div className="text-sm text-slate-500">{option.subtitle}</div>
                </button>
              ))}
            </div>

            <div className="rounded-[32px] border border-slate-200 bg-white/95 p-6 lg:p-8">
              <div className="text-sm uppercase tracking-[0.25em] text-slate-400 mb-4">This week on your device</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-3xl font-black text-teal-700">{weeklyInsights.checkIns}</div>
                  <div className="text-xs text-slate-500 mt-1">Mood check-ins</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-3xl font-black text-violet-700">{weeklyInsights.breathSessions}</div>
                  <div className="text-xs text-slate-500 mt-1">Breath sessions</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-3xl font-black text-fuchsia-700">{weeklyInsights.meditations}</div>
                  <div className="text-xs text-slate-500 mt-1">Meditations</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-3xl font-black text-emerald-700">
                    {weeklyInsights.moodDelta >= 0 ? '+' : ''}{weeklyInsights.moodDelta || '—'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Mood trend</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMainNav('you')}
                className="mt-6 text-sm font-bold text-teal-700 hover:underline"
              >
                View full insights in You →
              </button>
            </div>
          </section>
        )}

        {panicModeOpen && (
          <section className={`${overlayPanelClass} z-50 bg-[#fafcff]/95 backdrop-blur-xl`}>
            <div ref={panicModalRef} className="min-h-full max-w-4xl mx-auto px-5 py-8 pb-6 flex flex-col">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <div className="text-sm uppercase tracking-[0.3em] text-rose-600 mb-2">Panic Emergency Mode</div>
                  <h1 className="text-4xl md:text-6xl font-black leading-tight">You are safe right now.</h1>
                </div>
                <button
                  onClick={closePanicMode}
                  className="rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 text-slate-600 hover:bg-violet-100"
                >
                  Close
                </button>
              </div>

              <div className="rounded-[36px] border border-rose-200 bg-rose-50 p-6 mb-5">
                <div className="text-rose-700 font-bold mb-2">Important safety note</div>
                <p className="text-slate-600 leading-relaxed">
                  This tool is for calming and grounding. If you might harm yourself or someone else, or you feel medically unsafe, contact emergency services, a trusted person, Samaritans on 116 123, SHOUT by texting 85258, or Lifeline NI on 0808 808 8000.
                </p>
              </div>

              <div className="grid lg:grid-cols-[1fr_320px] gap-5 flex-1">
                <div className="rounded-[36px] border border-slate-200 bg-white/90 p-6 md:p-8">
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div>
                      <div className="text-sm text-slate-500 mb-1">Step {panicStep + 1} of {PANIC_STEPS.length}</div>
                      <h2 className="text-3xl md:text-5xl font-black text-teal-700">{PANIC_STEPS[panicStep].title}</h2>
                    </div>
                    <div className={`w-20 h-20 rounded-full border border-teal-300 bg-teal-400/15 flex items-center justify-center text-3xl ${calmMotion ? 'motion-soft animate-pulse' : ''}`}>
                      🫁
                    </div>
                  </div>

                  <p className="text-2xl text-slate-700 leading-relaxed mb-8">
                    {PANIC_STEPS[panicStep].instruction}
                  </p>

                  <label className="block mb-8">
                    <div className="text-sm text-slate-500 mb-3">{PANIC_STEPS[panicStep].prompt}</div>
                    <textarea
                      value={panicNotes[panicStep] || ''}
                      onChange={(event) => updatePanicNote(panicStep, event.target.value)}
                      placeholder="Type here, or just say it out loud."
                      className="w-full min-h-32 bg-white border border-slate-200 rounded-3xl p-5 text-xl text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                    />
                  </label>

                  <div className="rounded-3xl border border-emerald-200 bg-emerald-100 p-6 mb-8">
                    <div className="text-sm text-emerald-700/70 mb-2">Safety statement</div>
                    <div className="text-2xl font-bold text-emerald-800 leading-relaxed">
                      {SAFETY_STATEMENTS[panicStatementIndex]}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={previousPanicStep}
                      disabled={panicStep === 0}
                      className="rounded-2xl border border-slate-200 bg-white/90 py-4 font-bold text-slate-600 disabled:opacity-30"
                    >
                      Back
                    </button>
                    <button
                      onClick={nextPanicStep}
                      disabled={panicStep === PANIC_STEPS.length - 1}
                      className="rounded-2xl bg-teal-300 py-4 font-bold text-black disabled:opacity-40"
                    >
                      Next Step
                    </button>
                  </div>
                </div>

                <aside className="space-y-5">
                  <div className="rounded-[32px] border border-slate-200 bg-white/90 p-6">
                    <div className="text-sm text-slate-500 mb-3">Panic intensity</div>
                    <div className="text-5xl font-black text-orange-300 mb-4">{panicIntensity}</div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={panicIntensity}
                      onChange={(event) => setPanicIntensity(Number(event.target.value))}
                      className="w-full accent-orange-300"
                    />
                    <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                      Move this down as your body settles. Even a small drop counts.
                    </p>
                  </div>

                  <div className="rounded-[32px] border border-teal-300 bg-teal-100 p-6">
                    <h3 className="text-2xl font-black text-teal-800 mb-3">Quick breathing reset</h3>
                    <p className="text-slate-600 mb-5 leading-relaxed">
                      Try 3 physiological sighs: deep inhale, small top-up inhale, long slow exhale.
                    </p>
                    <button
                      onClick={() => {
                        setSelectedId('sigh');
                        closePanicMode();
                      }}
                      className="w-full rounded-2xl bg-teal-300 py-4 text-black font-bold"
                    >
                      Open Breathing Tool
                    </button>
                  </div>

                  <div className="rounded-[32px] border border-slate-200 bg-white/90 p-6">
                    <h3 className="text-xl font-black mb-3">Do this now</h3>
                    <div className="space-y-3 text-slate-600 leading-relaxed">
                      <p>Sit down if you can.</p>
                      <p>Put both feet on the floor.</p>
                      <p>Loosen your jaw and shoulders.</p>
                      <p>Exhale longer than you inhale.</p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>
        )}
        {mainNav === 'tools' && activeTool === 'calm' && (
          <section className="mb-8 rounded-[36px] border border-rose-200 bg-rose-50 p-6 lg:p-8">
            <h2 className="text-3xl font-black mb-3">Calm & grounding</h2>
            <p className="text-slate-600 max-w-2xl mb-6 leading-relaxed">
              For panic, overwhelm, or anxiety spikes. Opens a full-screen guide with safety statements and 5-4-3-2-1 grounding.
            </p>
            <button
              type="button"
              onClick={openPanicMode}
              className="rounded-2xl bg-rose-300 px-8 py-4 text-black font-black text-lg hover:scale-[1.02] transition-transform"
            >
              Open panic mode
            </button>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {SAFETY_STATEMENTS.slice(0, 4).map((line) => (
                <div key={line} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-600 text-sm leading-relaxed">
                  {line}
                </div>
              ))}
            </div>
          </section>
        )}

        {mainNav === 'tools' && activeTool === 'sounds' && (
          <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div>
                <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Sound library</div>
                <h2 className="text-4xl font-black mb-3">Tranquil sound tracks</h2>
                <p className="text-slate-600 max-w-2xl leading-relaxed">
                  Press play to listen anytime. Your pick also plays during breathing and meditation sessions.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={toggleSoundLibrary}
                  disabled={ambienceTrackId === 'none'}
                  className="rounded-2xl bg-sky-300 px-8 py-4 text-black font-black disabled:opacity-40"
                >
                  {soundLibraryPlaying ? 'Pause' : 'Play'} {selectedAmbience.emoji}
                </button>
                {soundLibraryPlaying && (
                  <button
                    type="button"
                    onClick={() => {
                      setSoundLibraryPlaying(false);
                      stopAmbience();
                    }}
                    className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-4 text-slate-600 font-bold hover:bg-violet-100"
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-8 max-w-xl">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-slate-600">Volume</span>
                <span className="font-mono text-slate-500">{meditationVolume}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={meditationVolume}
                onChange={(event) => setMeditationVolume(Number(event.target.value))}
                className="w-full accent-sky-400"
              />
              <p className="text-xs text-slate-500 mt-3">
                Now playing: <span className="font-semibold text-slate-700">{selectedAmbience.name}</span>
                {soundLibraryPlaying ? ' · listening' : ' · paused'}
              </p>
            </div>

            {['Nature', 'Cozy', 'Sleep', 'Focus', 'Meditation'].map((category) => {
              const tracks = listenableTracks.filter((track) => track.category === category);
              if (tracks.length === 0) return null;

              return (
                <div key={category} className="mb-10 last:mb-0">
                  <h3 className="text-sm uppercase tracking-[0.25em] text-slate-400 mb-4">{category}</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {tracks.map((track) => {
                      const active = ambienceTrackId === track.id;
                      return (
                        <button
                          key={track.id}
                          type="button"
                          onClick={() => previewSoundTrack(track.id)}
                          className={`rounded-2xl border p-4 text-left transition ${
                            active
                              ? 'border-sky-400/50 bg-sky-100/80 ring-2 ring-sky-300/40'
                              : 'border-slate-200 bg-white/90 hover:bg-violet-50'
                          }`}
                        >
                          <div className="text-3xl mb-2">{track.emoji}</div>
                          <div className="font-black mb-1">{track.name}</div>
                          <p className="text-xs text-slate-500 leading-relaxed">{track.description}</p>
                          <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400">
                            {track.file ? 'CC0 field recording' : 'Generated utility tone'}
                          </div>
                          {active && soundLibraryPlaying && (
                            <div className="mt-2 text-xs font-bold text-sky-700">▶ Playing</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {mainNav === 'tools' && activeTool === 'breathe' && (
        <>
                <header className="grid lg:grid-cols-[1fr_440px] gap-10 items-center mb-14">
          <section>
            <div className="inline-flex items-center gap-2 border border-slate-200 bg-violet-100/60 px-4 py-2 rounded-full text-sm text-slate-600 mb-6 backdrop-blur">
              <span className={`w-2 h-2 rounded-full ${colours.bg} animate-pulse`} />
              Functional breathing engine active
            </div>

            <h1 className="text-5xl md:text-7xl font-black leading-[0.95] tracking-tight mb-6">
              Breathe with
              <span className={`block ${colours.text}`}>guided rhythm.</span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-4">
              Choose a breathing technique, press start, and follow the animated circle. Soft chimes mark each inhale, hold, and exhale.
            </p>

            <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 mb-8 cursor-pointer">
              <input
                type="checkbox"
                checked={breathAmbienceEnabled}
                onChange={(event) => {
                  setBreathAmbienceEnabled(event.target.checked);
                  if (!event.target.checked && !meditationRunning) stopAmbience();
                }}
                className="w-5 h-5 accent-teal-500"
              />
              <span className="text-sm text-slate-600">
                Background sound while breathing ({selectedAmbience.emoji} {selectedAmbience.name})
              </span>
            </label>

            <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
              {MOODS.slice(0, 6).map((mood) => (
                <button
                  key={mood.label}
                  onClick={() => selectTechnique(mood.technique)}
                  className="rounded-2xl border border-slate-200 bg-white/90 px-4 py-4 text-left hover:bg-violet-100 transition"
                >
                  <div className="text-sm text-slate-500 mb-1">I feel</div>
                  <div className="font-semibold">{mood.label}</div>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-[36px] border border-slate-200 bg-white/90 backdrop-blur-xl p-5 shadow-2xl">
            <div className="rounded-[28px] border border-slate-200 bg-white/85 p-6 min-h-[680px] flex flex-col">
              <div className="flex items-start justify-between gap-4 mb-7">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Current session</div>
                  <h2 className="text-2xl font-black">{selected.name}</h2>
                  <p className="text-sm text-slate-500 mt-1">{selected.subtitle}</p>
                </div>
                <div className={`rounded-2xl ${colours.soft} ${colours.text} border ${colours.border} px-3 py-2 font-mono text-sm`}>
                  {formatTime(Math.max(0, totalSessionSeconds - elapsedSeconds))}
                </div>
              </div>

              <div className="mb-6">
                <div className="h-2 rounded-full bg-slate-200/80 overflow-hidden">
                  <div
                    className={`h-full ${colours.bg} transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-2 font-mono">
                  <span>Round {round}/{selected.rounds}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center py-8">
                <div className="relative w-72 h-72 flex items-center justify-center mb-8">
                  <div className={`absolute inset-4 rounded-full ${colours.soft} blur-2xl transition-all duration-1000 ${isRunning ? 'opacity-100' : 'opacity-40'}`} />
                  <div className="absolute inset-0 rounded-full border border-slate-200" />
                  <div className="absolute inset-8 rounded-full border border-slate-200" />
                  <div className="absolute inset-16 rounded-full border border-slate-200" />

                  <div
                    className={`relative w-40 h-40 rounded-full ${colours.soft} border ${colours.border} shadow-2xl ${colours.ring} flex items-center justify-center motion-soft ${calmMotion ? 'transition-transform ease-in-out' : ''}`}
                    style={{
                      transform: `scale(${completed ? 1 : phase.scale})`,
                      transitionDuration: calmMotion ? `${phase.seconds}s` : '0s'
                    }}
                  >
                    <div className={`w-24 h-24 rounded-full ${colours.bg} opacity-25 ${calmMotion ? 'animate-pulse motion-soft' : ''}`} />
                  </div>

                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                    <div className={`text-5xl font-black font-mono ${colours.text}`}>
                      {completed ? '✓' : secondsLeft}
                    </div>
                    <div className="text-slate-500 uppercase tracking-[0.3em] text-xs mt-2">
                      {completed ? 'Complete' : phase.label}
                    </div>
                  </div>
                </div>

                <div className="text-center max-w-sm mb-8">
                  <div className={`text-3xl font-black mb-3 ${colours.text}`}>
                    {completed ? 'Session complete' : phase.label}
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    {completed ? 'Well done. Notice how your body and mind feel now.' : phase.instruction}
                  </p>
                </div>

                <div className="flex flex-wrap justify-center gap-2 mb-8">
                  {selected.pattern.map((item, index) => (
                    <div
                      key={`${item.label}-${index}`}
                      className={`rounded-full border px-3 py-2 text-xs font-mono transition ${
                        index === phaseIndex && !completed
                          ? `${colours.border} ${colours.soft} ${colours.text}`
                          : 'border-slate-200 text-slate-400 bg-white/85'
                      }`}
                    >
                      {item.label} {item.seconds}s
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-3">
                {!isRunning ? (
                  <button
                    onClick={startSession}
                    className={`rounded-2xl py-4 px-6 ${colours.bg} text-black font-bold hover:scale-[1.02] transition-transform`}
                  >
                    {completed ? 'Repeat Session' : 'Start Breathing'}
                  </button>
                ) : (
                  <button
                    onClick={pauseSession}
                    className="rounded-2xl py-4 px-6 bg-white text-black font-bold hover:scale-[1.02] transition-transform"
                  >
                    Pause
                  </button>
                )}

                <button
                  onClick={resetSession}
                  className="rounded-2xl py-4 px-5 border border-slate-200 bg-white/90 text-slate-600 hover:bg-violet-100 transition"
                >
                  Reset
                </button>
              </div>
            </div>
          </section>
        </header>

        <section className="mb-16">
          <div className="flex items-end justify-between gap-6 mb-7">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Breathwork Library</div>
              <h2 className="text-4xl font-black">Choose a technique</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
            {BREATHING_TECHNIQUES.map((technique) => {
              const itemColours = colourClasses(technique.color);
              const active = selectedId === technique.id;

              return (
                <button
                  key={technique.id}
                  onClick={() => selectTechnique(technique.id)}
                  className={`rounded-3xl border p-5 text-left transition hover:-translate-y-1 ${
                    active
                      ? `${itemColours.border} ${itemColours.soft}`
                      : 'border-slate-200 bg-white/85 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-2xl ${itemColours.soft} ${itemColours.text} border ${itemColours.border} flex items-center justify-center text-2xl mb-5`}>
                    💨
                  </div>

                  <h3 className="font-black text-xl mb-2">{technique.name}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">{technique.subtitle}</p>

                  <div className="flex flex-wrap gap-2">
                    {technique.pattern.map((step, index) => (
                      <span key={`${step.label}-${index}`} className="rounded-full bg-white border border-slate-200 px-2 py-1 text-[11px] text-slate-500 font-mono">
                        {step.seconds}s
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
        </>)}

        {mainNav === 'you' && (
        <>
        <section className="mb-8 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Your week</div>
          <h2 className="text-4xl font-black mb-6">Local insights</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="rounded-3xl border border-teal-200 bg-teal-50 p-5">
              <div className="text-4xl font-black text-teal-700">{weeklyInsights.checkIns}</div>
              <div className="text-sm text-slate-500 mt-1">Mood check-ins (7 days)</div>
            </div>
            <div className="rounded-3xl border border-violet-200 bg-violet-50 p-5">
              <div className="text-4xl font-black text-violet-700">{weeklyInsights.cbtEntries}</div>
              <div className="text-sm text-slate-500 mt-1">CBT journal entries</div>
            </div>
            <div className="rounded-3xl border border-fuchsia-200 bg-fuchsia-50 p-5">
              <div className="text-4xl font-black text-fuchsia-700">{weeklyInsights.meditations}</div>
              <div className="text-sm text-slate-500 mt-1">Meditations completed</div>
            </div>
            <div className="rounded-3xl border border-sky-200 bg-sky-50 p-5">
              <div className="text-4xl font-black text-sky-700">{weeklyInsights.breathSessions}</div>
              <div className="text-sm text-slate-500 mt-1">Breath sessions (all time)</div>
            </div>
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="text-4xl font-black text-emerald-700">{weeklyInsights.moodAvg || '—'}</div>
              <div className="text-sm text-slate-500 mt-1">Average mood this week</div>
            </div>
            <div className="rounded-3xl border border-blue-200 bg-blue-50 p-5">
              <div className="text-4xl font-black text-blue-700">{weeklyInsights.anxietyAvg || '—'}</div>
              <div className="text-sm text-slate-500 mt-1">Average anxiety this week</div>
            </div>
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            All stats are computed on this device. Nothing is sent to a server.
            {weeklyInsights.moodDelta !== 0 && (
              <> Mood trend this week: {weeklyInsights.moodDelta >= 0 ? '+' : ''}{weeklyInsights.moodDelta} since your earliest check-in.</>
            )}
          </p>
        </section>

        <div className="mb-8 rounded-[28px] border border-slate-200 bg-white/90 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-500">{currentUser.email || 'Guest mode'} · {currentUser.plan} plan</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => openAuth('signin')} className="rounded-xl bg-teal-300 px-4 py-2 text-black font-bold text-sm">Sign in</button>
            <button type="button" onClick={() => openAuth('signup')} className="rounded-xl border border-slate-200 px-4 py-2 text-slate-600 text-sm">Create account</button>
            {isInstallable && (
              <button type="button" onClick={installApp} className="rounded-xl bg-emerald-300 px-4 py-2 text-black font-bold text-sm">Install app</button>
            )}
          </div>
        </div>

        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Offline App Support</div>
              <h2 className="text-4xl font-black mb-3">PWA-ready offline mode</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Mind Wanders includes installable app support, consent-based local storage, service-worker caching and a sync queue for when a backend is added.
              </p>
            </div>
            <div className={`hidden md:block rounded-3xl border px-5 py-4 text-center min-w-36 ${isOnline ? 'border-emerald-200 bg-emerald-100' : 'border-orange-400/20 bg-orange-400/10'}`}>
              <div className={`text-3xl font-black ${isOnline ? 'text-emerald-700' : 'text-orange-200'}`}>{isOnline ? 'ON' : 'OFF'}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">network</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_380px] gap-6 mb-16">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                {OFFLINE_FEATURES.map((feature) => (
                  <div key={feature} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                    <div className="text-2xl mb-3">✅</div>
                    <div className="font-bold text-slate-700">{feature}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-teal-300 bg-teal-100 p-6 mb-6">
                <h3 className="text-2xl font-black text-teal-800 mb-4">Install settings</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-slate-500 mb-1">App name</div>
                    <div className="font-mono text-slate-600">{PWA_MANIFEST.name}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-slate-500 mb-1">Display mode</div>
                    <div className="font-mono text-slate-600">{PWA_MANIFEST.display}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-slate-500 mb-1">Theme colour</div>
                    <div className="font-mono text-slate-600">{PWA_MANIFEST.theme_color}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-slate-500 mb-1">Service worker</div>
                    <div className="font-mono text-slate-600">{swStatus}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="text-2xl font-black mb-4">Offline files</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Cached after first visit: <span className="font-mono text-teal-800/80">index.html</span>,{' '}
                  <span className="font-mono text-teal-800/80">mindflow_react_app_mvp.jsx</span>,{' '}
                  <span className="font-mono text-teal-800/80">manifest.json</span>, React, Babel, and Tailwind CDN scripts.
                  Visit once online, then reload offline to test.
                </p>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Sync queue</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">
                  When storage is accepted, offline CBT and mood entries are saved locally and queued for future backend sync.
                </p>
                {offlineQueue.length === 0 ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-100 p-4 text-emerald-800">No unsynced changes.</div>
                ) : (
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {offlineQueue.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="font-bold mb-1">{item.type}</div>
                        <div className="text-xs text-slate-400">{new Date(item.createdAt).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Production checklist</h3>
                <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
                  <p>✓ manifest.json and service-worker.js are in the project root.</p>
                  <p>✓ Web, PWA, Android, and iOS icons generated (Capacitor + PNG/WebP).</p>
                  <p>✓ Cookie/storage consent and wipe controls added.</p>
                  <p>○ Publish full privacy policy and terms pages before collecting account data.</p>
                  <p>○ Use IndexedDB for large encrypted offline journals.</p>
                  <p>○ Sync queued changes when Firebase/Supabase is online.</p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="mb-8 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Privacy & UK GDPR</div>
              <h2 className="text-4xl font-black mb-3">Your data stays on this device</h2>
              <p className="text-slate-600 max-w-3xl leading-relaxed">
                Mind Wanders currently has no production backend, no advertising cookies and no third-party analytics.
                Personal app data is stored locally in your browser only when you accept optional storage. Browser cache may store
                app files and audio so the app loads quickly and can work offline.
              </p>
            </div>
            <div className="rounded-3xl border border-teal-200 bg-teal-50 p-5 min-w-64">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Current choice</div>
              <div className="text-2xl font-black text-teal-700 capitalize">{storageConsent}</div>
              <div className="mt-2 text-sm text-slate-500">
                {canStoreUserData ? 'Optional local storage is enabled.' : 'Optional local storage is disabled until accepted.'}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3 mb-6">
            {DATA_STORAGE_ITEMS.map((item) => (
              <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="font-black mb-1">{item.label}</div>
                <div className="text-xs uppercase tracking-widest text-teal-700 mb-2">{item.storage}</div>
                <p className="text-sm text-slate-500 leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={acceptDataStorage} className="rounded-2xl bg-teal-300 text-teal-900 font-black px-6 py-4">
              Accept cookies/storage
            </button>
            <button type="button" onClick={rejectDataStorage} className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-4 text-slate-600 font-bold hover:bg-violet-100">
              Reject optional cookies/storage
            </button>
            <button type="button" onClick={wipeAllStoredInformation} className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-4 text-rose-700 font-black hover:bg-rose-100">
              Wipe all stored information
            </button>
          </div>

          {privacyMessage && (
            <div className="mt-5 rounded-2xl border border-teal-200 bg-teal-50 p-4 text-teal-800">
              {privacyMessage}
            </div>
          )}
        </section>

        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Profile & Account Settings</div>
              <h2 className="text-4xl font-black mb-3">Personal account hub</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Manage profile details, preferences, emergency contact and future sync settings. This is ready to connect to Firebase or Supabase authentication.
              </p>
            </div>
            <div className="hidden md:block rounded-3xl border border-teal-300 bg-teal-100 px-5 py-4 text-center min-w-36">
              <div className="text-3xl font-black text-teal-700">{currentUser.avatar}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">profile</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_380px] gap-6 mb-16">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid md:grid-cols-2 gap-5 mb-6">
                <label className="block">
                  <div className="text-sm text-slate-500 mb-2">Display name</div>
                  <input value={profileForm.name} onChange={(event) => updateProfileField('name', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" />
                </label>
                <label className="block">
                  <div className="text-sm text-slate-500 mb-2">Email</div>
                  <input value={profileForm.email} onChange={(event) => updateProfileField('email', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" placeholder="you@example.com" />
                </label>
                <label className="block">
                  <div className="text-sm text-slate-500 mb-2">Avatar emoji</div>
                  <input value={profileForm.avatar} onChange={(event) => updateProfileField('avatar', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" />
                </label>
                <label className="block">
                  <div className="text-sm text-slate-500 mb-2">Emergency contact</div>
                  <input value={profileForm.preferences?.emergencyContact || ''} onChange={(event) => updateProfilePreference('emergencyContact', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-teal-400" placeholder="Name or phone number" />
                </label>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-black mb-1">Low stimulation</div>
                    <div className="text-sm text-slate-500">Reduce visual intensity</div>
                  </div>
                  <input type="checkbox" checked={profileForm.preferences?.lowStimulation || false} onChange={(event) => updateProfilePreference('lowStimulation', event.target.checked)} className="w-5 h-5 accent-teal-300" />
                </label>
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-black mb-1">Dark mode</div>
                    <div className="text-sm text-slate-500">Switch the app theme</div>
                  </div>
                  <input type="checkbox" checked={darkMode} onChange={(event) => setDarkMode(event.target.checked)} className="w-5 h-5 accent-teal-300" />
                </label>
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-black mb-1">Breath haptics</div>
                    <div className="text-sm text-slate-500">Gentle vibration on phase change (mobile)</div>
                  </div>
                  <input type="checkbox" checked={profileForm.preferences?.haptics !== false} onChange={(event) => updateProfilePreference('haptics', event.target.checked)} className="w-5 h-5 accent-teal-300" />
                </label>
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-black mb-1">Cloud sync</div>
                    <div className="text-sm text-slate-500">Backend-ready setting</div>
                  </div>
                  <input type="checkbox" checked={profileForm.preferences?.dataSync || false} onChange={(event) => updateProfilePreference('dataSync', event.target.checked)} className="w-5 h-5 accent-teal-300" />
                </label>
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="font-black mb-2">Daily reminder</div>
                  <input type="time" value={profileForm.preferences?.reminderTime || '09:00'} onChange={(event) => updateProfilePreference('reminderTime', event.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl p-3 text-slate-700 outline-none focus:border-teal-400" />
                </label>
              </div>

              {authMessage && <div className="rounded-2xl border border-teal-300 bg-teal-100 p-4 text-teal-800 mb-5">{authMessage}</div>}

              <div className="flex flex-wrap gap-3">
                <button onClick={saveProfile} className="rounded-2xl bg-teal-300 text-teal-900 font-black px-6 py-4">Save Profile</button>
                <button onClick={() => openAuth('signin')} className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-4 text-slate-600 hover:bg-violet-100">Open Login</button>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Backend connection plan</h3>
                <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
                  <p>Replace local demo login with Firebase Auth or Supabase Auth.</p>
                  <p>Store journals, moods, routines and sessions under the user ID.</p>
                  <p>Add email verification, password reset and OAuth login.</p>
                  <p>Use Stripe or RevenueCat later for premium accounts.</p>
                </div>
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Current data</h3>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-2xl font-black">{entries.length}</div><div className="text-xs text-slate-400">CBT</div></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-2xl font-black">{moodEntries.length}</div><div className="text-xs text-slate-400">Mood</div></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-2xl font-black">{meditationSessions.length}</div><div className="text-xs text-slate-400">Meditation</div></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-2xl font-black">{routineSaved.length}</div><div className="text-xs text-slate-400">Routines</div></div>
                </div>
              </div>
            </aside>
          </div>
        </section>

                <section className="mb-8 rounded-[36px] border border-slate-200 bg-white/85 p-8 lg:p-10 backdrop-blur-xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Safety</div>
              <h2 className="text-3xl font-black mb-4">Gentle support, not crisis care</h2>
              <p className="text-slate-600 leading-relaxed">
                These tools support self-regulation, not emergency care. Stop breathwork if dizzy or unsafe. If anyone is at risk, call emergency services, Samaritans on 116 123, SHOUT by texting 85258, or Lifeline NI on 0808 808 8000.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Sit or lie down for breathwork',
                'Never force breath holds',
                'Pause when dizzy',
                'Use crisis support in emergencies'
              ].map((item) => (
                <div key={item} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-600 text-sm">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>
        </>)}

        {mainNav === 'tools' && activeTool === 'plan' && (
        <>
        
        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">AI-Assisted Emotional Routines</div>
              <h2 className="text-4xl font-black mb-3">Personalised routine builder</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Select the user’s emotional state, intensity, time available and support style. The app generates a step-by-step routine using breathwork, grounding, CBT, meditation and ADHD tools.
              </p>
            </div>
            <div className="hidden md:block rounded-3xl border border-emerald-200 bg-emerald-100 px-5 py-4 text-center min-w-36">
              <div className="text-3xl font-black text-emerald-700">{routineTotalMinutes}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">minutes</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_420px] gap-6 mb-16">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
                {EMOTIONAL_STATES.map((state) => {
                  const active = routineState === state.id;
                  return (
                    <button
                      key={state.id}
                      onClick={() => setRoutineState(state.id)}
                      className={`rounded-2xl border p-4 text-left transition ${
                        active
                          ? 'border-emerald-400/40 bg-emerald-400/15'
                          : 'border-slate-200 bg-slate-50 hover:bg-violet-100'
                      }`}
                    >
                      <div className="text-3xl mb-3">{state.emoji}</div>
                      <div className={active ? 'font-black text-emerald-700' : 'font-black'}>{state.label}</div>
                    </button>
                  );
                })}
              </div>

              <div className="grid lg:grid-cols-3 gap-5 mb-6">
                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="text-sm text-slate-500 mb-3">Goal</div>
                  <select
                    value={routineGoal}
                    onChange={(event) => setRoutineGoal(event.target.value)}
                    className="w-full bg-white/90 border border-slate-200 rounded-2xl p-4 text-slate-700 outline-none focus:border-emerald-400/40"
                  >
                    {ROUTINE_GOALS.map((goal) => <option key={goal}>{goal}</option>)}
                  </select>
                </label>

                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-500">Time available</span>
                    <span className="font-mono text-emerald-700">{routineMinutes} min</span>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="30"
                    value={routineMinutes}
                    onChange={(event) => setRoutineMinutes(Number(event.target.value))}
                    className="w-full accent-emerald-300"
                  />
                </label>

                <label className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-500">Intensity</span>
                    <span className="font-mono text-orange-200">{routineIntensity}/100</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={routineIntensity}
                    onChange={(event) => setRoutineIntensity(Number(event.target.value))}
                    className="w-full accent-orange-300"
                  />
                </label>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 mb-6">
                <div className="text-sm text-slate-500 mb-4">Support style</div>
                <div className="flex flex-wrap gap-2">
                  {SUPPORT_STYLES.map((style) => (
                    <button
                      key={style}
                      onClick={() => setRoutineSupportStyle(style)}
                      className={`rounded-full border px-4 py-2 text-sm transition ${
                        routineSupportStyle === style
                          ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-700'
                          : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-violet-100'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-[32px] border border-emerald-200 bg-emerald-100 p-6 mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-5 mb-6">
                  <div>
                    <div className="text-sm uppercase tracking-[0.3em] text-emerald-700/60 mb-2">Generated Plan</div>
                    <h3 className="text-3xl font-black text-emerald-800">{routineStateLabel} → {routineGoal}</h3>
                    <p className="text-slate-500 mt-2">Tone: {routineSupportStyle}. Intensity: {routineIntensity}/100.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button type="button" onClick={saveGeneratedRoutine} className="rounded-2xl bg-emerald-300 text-black font-black px-5 py-3 whitespace-nowrap">
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => startRoutineRunner(generatedRoutine)}
                      className="rounded-2xl border border-emerald-400 bg-white px-5 py-3 font-black text-emerald-800 whitespace-nowrap"
                    >
                      Start guided run
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {generatedRoutine.map((step, index) => (
                    <div key={`${step.tool}-${index}`} className="rounded-3xl border border-slate-200 bg-violet-50/80 p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Step {index + 1} • {step.type}</div>
                          <div className="text-2xl font-black">{step.tool}</div>
                        </div>
                        <div className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-sm font-mono text-emerald-800">
                          {step.minutes}m
                        </div>
                      </div>
                      <p className="text-slate-600 leading-relaxed mb-4">{step.action}</p>
                      <button
                        onClick={() => openRoutineTool(step)}
                        className="rounded-2xl border border-slate-200 bg-white/[0.05] px-4 py-3 text-sm text-slate-600 hover:bg-violet-100"
                      >
                        Open related tool
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-3">Why this routine?</h3>
                <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
                  <p>High intensity adds a safety pause before deeper work.</p>
                  <p>Anxiety and panic prioritise grounding and longer exhales.</p>
                  <p>ADHD and overwhelm prioritise task reduction and distraction capture.</p>
                  <p>Low mood uses behavioural activation and tiny achievable steps.</p>
                </div>
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Saved routines</h3>
                {routineSaved.length === 0 ? (
                  <p className="text-slate-500 leading-relaxed">No saved routines yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {routineSaved.map((routine) => (
                      <div key={routine.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs text-slate-400 mb-2">{new Date(routine.createdAt).toLocaleString()}</div>
                        <div className="font-black mb-1">{routine.state} → {routine.goal}</div>
                        <div className="text-sm text-slate-500 mb-3">{routine.minutes} min • {routine.supportStyle} • {routine.steps.length} steps</div>
                        <div className="space-y-1 text-xs text-slate-500">
                          {routine.steps.slice(0, 3).map((step, index) => <div key={`${routine.id}-${index}`}>• {step.tool}</div>)}
                        </div>
                        <button
                          type="button"
                          onClick={() => startRoutineRunner(routine.steps)}
                          className="mt-3 rounded-xl bg-emerald-200 px-3 py-2 text-xs font-bold text-emerald-900"
                        >
                          Run this routine
                        </button>
                        <button onClick={() => deleteSavedRoutine(routine.id)} className="mt-2 block text-xs text-rose-600 hover:text-rose-700">Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
        </>)}

        {mainNav === 'tools' && activeTool === 'meditate' && (
        <>
        
        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Sound & Meditation</div>
              <h2 className="text-4xl font-black mb-3">Guided meditation player</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Read the on-screen prompts at your own pace. Layered rain, ocean, or soft noise plays underneath, with gentle chimes to bring you back.
              </p>
            </div>
            <div className="hidden md:block rounded-3xl border border-fuchsia-400/20 bg-fuchsia-400/10 px-5 py-4 text-center min-w-36">
              <div className="text-3xl font-black text-fuchsia-200">{meditationSessions.length}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">sessions</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_380px] gap-6 mb-16">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
                {MEDITATION_PRESETS.map((preset) => {
                  const active = meditationId === preset.id;
                  const ambience = AMBIENCE_OPTIONS.find((item) => item.id === preset.ambience);
                  return (
                    <button
                      key={preset.id}
                      onClick={() => {
                        setMeditationId(preset.id);
                        setAmbienceTrackId(preset.ambience);
                      }}
                      className={`rounded-2xl border p-4 text-left transition ${
                        active
                          ? 'border-fuchsia-400/40 bg-fuchsia-400/15'
                          : 'border-slate-200 bg-slate-50 hover:bg-violet-100'
                      }`}
                    >
                      <div className="text-2xl mb-3">{ambience?.emoji}</div>
                      <div className={active ? 'font-black text-fuchsia-200 mb-1' : 'font-black mb-1'}>{preset.name}</div>
                      <div className="text-xs text-slate-500 leading-relaxed mb-3">{preset.description}</div>
                      <div className="font-mono text-xs text-slate-500">{preset.minutes} min • {ambience?.name}</div>
                    </button>
                  );
                })}
              </div>

              <div className="rounded-[36px] border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-400/15 to-indigo-400/10 p-6 md:p-8 text-center mb-6">
                <div className="text-sm uppercase tracking-[0.3em] text-fuchsia-200/60 mb-3">Now Playing</div>
                <h3 className="text-3xl md:text-5xl font-black text-fuchsia-100 mb-3">{selectedMeditation.name}</h3>
                <p className="text-slate-600 max-w-2xl mx-auto mb-8">{selectedMeditation.description}</p>

                <div className="relative w-72 h-72 max-w-full mx-auto flex items-center justify-center mb-8">
                  <div className={`absolute inset-0 rounded-full bg-fuchsia-400/10 blur-3xl ${meditationRunning && calmMotion ? 'animate-pulse motion-soft' : ''}`} />
                  <div className="absolute inset-4 rounded-full border border-fuchsia-300/10" />
                  <div className="absolute inset-12 rounded-full border border-fuchsia-300/10" />
                  <div className="relative w-44 h-44 rounded-full border border-fuchsia-300/30 bg-fuchsia-300/10 flex flex-col items-center justify-center shadow-2xl shadow-fuchsia-400/10">
                    <div className="text-5xl font-black font-mono text-fuchsia-100">{formatTime(meditationSecondsLeft)}</div>
                    <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mt-2">remaining</div>
                  </div>
                </div>

                <div className="h-3 rounded-full bg-white overflow-hidden mb-6">
                  <div className="h-full bg-fuchsia-300 transition-all duration-500" style={{ width: `${meditationProgress}%` }} />
                </div>

                <div className="rounded-3xl border border-slate-200 bg-violet-50/80 p-5 text-xl text-slate-700 leading-relaxed mb-6">
                  {currentMeditationPrompt}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {!meditationRunning ? (
                    <button onClick={startMeditation} className="rounded-2xl bg-fuchsia-300 py-4 text-black font-black col-span-2">
                      {meditationSecondsLeft === 0 ? 'Repeat' : 'Start'}
                    </button>
                  ) : (
                    <button onClick={pauseMeditation} className="rounded-2xl bg-white py-4 text-black font-black col-span-2">
                      Pause
                    </button>
                  )}
                  <button onClick={resetMeditation} className="rounded-2xl border border-slate-200 bg-white/90 py-4 text-slate-600 hover:bg-violet-100">
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Audio controls</h3>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-4">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-600">Selected track</span>
                    <span className="text-fuchsia-200">{selectedAmbience.emoji} {selectedAmbience.name}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    <button type="button" onClick={() => navigateToTool('sounds')} className="text-sky-700 font-semibold hover:underline">
                      Open Sounds
                    </button>
                    {' '}to browse all {listenableTracks.length} tracks.
                  </p>
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-600">Volume</span>
                    <span className="font-mono text-slate-500">{meditationVolume}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={meditationVolume}
                    onChange={(event) => setMeditationVolume(Number(event.target.value))}
                    className="w-full accent-fuchsia-300"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-600">Interval bell</span>
                    <span className="font-mono text-slate-500">Every {meditationBellEvery} min</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={meditationBellEvery}
                    onChange={(event) => setMeditationBellEvery(Number(event.target.value))}
                    className="w-full accent-fuchsia-300"
                  />
                </div>
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Meditation history</h3>
                {meditationSessions.length === 0 ? (
                  <p className="text-slate-500 leading-relaxed">No completed meditation sessions yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {meditationSessions.map((session) => (
                      <div key={session.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs text-slate-400 mb-2">{new Date(session.completedAt).toLocaleString()}</div>
                        <div className="font-black mb-1">{session.name}</div>
                        <div className="text-sm text-slate-500">{session.minutes} minutes completed</div>
                        <button onClick={() => deleteMeditationSession(session.id)} className="mt-3 text-xs text-rose-600 hover:text-rose-700">Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
        </>)}

        {mainNav === 'tools' && activeTool === 'mood' && (
        <>
        
        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">Mood Tracking Dashboard</div>
              <h2 className="text-4xl font-black mb-3">Daily mental health check-in</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Track mood, anxiety, focus, energy, sleep and stress so users can spot patterns and choose the right support tools.
              </p>
            </div>
            <div className="hidden md:block rounded-3xl border border-teal-300 bg-teal-100 px-5 py-4 text-center min-w-36">
              <div className="text-3xl font-black text-teal-700">{moodEntries.length}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">check-ins</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_420px] gap-6 mb-16">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid md:grid-cols-2 gap-5 mb-6">
                {MOOD_FACTORS.map((factor) => (
                  <div key={factor.key} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{factor.emoji}</span>
                        <div>
                          <div className="font-black">{factor.label}</div>
                          <div className="text-xs text-slate-400">{factor.low} → {factor.high}</div>
                        </div>
                      </div>
                      <div className="font-mono text-teal-700 text-xl">{moodCheckIn[factor.key]}</div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={moodCheckIn[factor.key]}
                      onChange={(event) => updateMoodCheckIn(factor.key, Number(event.target.value))}
                      className="w-full accent-teal-300"
                    />
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-5 mb-5">
                <div className="text-sm text-slate-500 mb-4">What affected today?</div>
                <div className="flex flex-wrap gap-2">
                  {MOOD_TAGS.map((tag) => {
                    const active = moodCheckIn.tags.includes(tag);
                    return (
                      <button
                        key={tag}
                        onClick={() => toggleMoodTag(tag)}
                        className={`rounded-full border px-4 py-2 text-sm transition ${
                          active
                            ? 'border-teal-400 bg-teal-400/15 text-teal-700'
                            : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-violet-100'
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-5 mb-6">
                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">Notes</div>
                  <textarea
                    value={moodCheckIn.note}
                    onChange={(event) => updateMoodCheckIn('note', event.target.value)}
                    placeholder="What was going on today?"
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>

                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">One win</div>
                  <textarea
                    value={moodCheckIn.win}
                    onChange={(event) => updateMoodCheckIn('win', event.target.value)}
                    placeholder="Something you did, however small."
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>

                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">Support needed</div>
                  <textarea
                    value={moodCheckIn.support}
                    onChange={(event) => updateMoodCheckIn('support', event.target.value)}
                    placeholder="What would help tomorrow?"
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={saveMoodCheckIn}
                  className="rounded-2xl bg-teal-300 text-teal-900 font-bold px-6 py-4 hover:scale-[1.02] transition-transform"
                >
                  Save Check-In
                </button>
                <button
                  onClick={newMoodCheckIn}
                  className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-4 text-slate-600 hover:bg-violet-100 transition"
                >
                  New Check-In
                </button>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-3xl border border-emerald-200 bg-emerald-100 p-5">
                  <div className="text-sm text-emerald-700/70 mb-2">Mood trend</div>
                  <div className="text-4xl font-black text-emerald-700">{moodTrend >= 0 ? '+' : ''}{moodTrend}</div>
                  <div className="text-xs text-slate-500 mt-2">Since previous check-in</div>
                </div>
                <div className="rounded-3xl border border-blue-400/20 bg-blue-400/10 p-5">
                  <div className="text-sm text-blue-200/70 mb-2">Anxiety change</div>
                  <div className="text-4xl font-black text-blue-200">{anxietyTrend >= 0 ? '+' : ''}{anxietyTrend}</div>
                  <div className="text-xs text-slate-500 mt-2">Positive means lower anxiety</div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">7-check-in averages</h3>
                <div className="space-y-4">
                  {MOOD_FACTORS.map((factor) => (
                    <div key={factor.key}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">{factor.emoji} {factor.label}</span>
                        <span className="font-mono text-slate-500">{moodAverages[factor.key] || 0}</span>
                      </div>
                      <div className="h-2 rounded-full bg-white overflow-hidden">
                        <div className="h-full bg-teal-300" style={{ width: `${moodAverages[factor.key] || 0}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">History</h3>
                {moodEntries.length === 0 ? (
                  <p className="text-slate-500 leading-relaxed">No mood check-ins yet. Save your first one to start seeing patterns.</p>
                ) : (
                  <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {moodEntries.map((entry) => (
                      <div key={entry.id} className={`rounded-2xl border p-4 ${activeMoodId === entry.id ? 'border-teal-400 bg-teal-100' : 'border-slate-200 bg-slate-50'}`}>
                        <button onClick={() => loadMoodEntry(entry)} className="text-left w-full">
                          <div className="text-xs text-slate-400 mb-2">{new Date(entry.updatedAt).toLocaleString()}</div>
                          <div className="grid grid-cols-3 gap-2 font-mono text-sm mb-3">
                            <span>🙂 {entry.mood}</span>
                            <span>🌊 {entry.anxiety}</span>
                            <span>🎯 {entry.focus}</span>
                          </div>
                          <div className="text-sm text-slate-500 line-clamp-2">{entry.note || entry.win || 'No note added'}</div>
                        </button>
                        <button onClick={() => deleteMoodEntry(entry.id)} className="mt-3 text-xs text-rose-600 hover:text-rose-700">Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
        </>)}

        {mainNav === 'tools' && activeTool === 'focus' && (
        <>
        
        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">ADHD Focus Timers</div>
              <h2 className="text-4xl font-black mb-3">Focus sprint system</h2>
              <p className="text-slate-600 max-w-2xl leading-relaxed">
                Pick a timer style, write one clear task, park distractions as they appear, and move through focus and recovery blocks.
              </p>
            </div>
            <div className="hidden md:block rounded-3xl border border-amber-400/20 bg-amber-400/10 px-5 py-4 text-center min-w-36">
              <div className="text-3xl font-black text-amber-200">{focusCompleted}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">sprints done</div>
            </div>
          </div>

          <div className="grid xl:grid-cols-[1fr_380px] gap-6">
            <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
              <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
                {FOCUS_PRESETS.map((preset) => {
                  const active = focusPresetId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => setFocusPresetId(preset.id)}
                      className={`rounded-2xl border p-4 text-left transition ${
                        active
                          ? 'border-amber-400/40 bg-amber-400/15'
                          : 'border-slate-200 bg-slate-50 hover:bg-violet-100'
                      }`}
                    >
                      <div className={active ? 'font-black text-amber-200 mb-1' : 'font-black mb-1'}>{preset.name}</div>
                      <div className="text-xs text-slate-500 leading-relaxed mb-3">{preset.description}</div>
                      <div className="font-mono text-xs text-slate-500">
                        {preset.focusMinutes}m focus • {preset.breakMinutes}m break • {preset.rounds}x
                      </div>
                    </button>
                  );
                })}
              </div>

              <label className="block mb-6">
                <div className="text-sm text-slate-500 mb-3">One task for this sprint</div>
                <input
                  value={focusTask}
                  onChange={(event) => setFocusTask(event.target.value)}
                  placeholder="Example: Write the first paragraph, tidy one drawer, reply to one email..."
                  className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-amber-400/40"
                />
              </label>

              <div className="rounded-[32px] border border-amber-400/20 bg-amber-400/10 p-6 md:p-8 text-center mb-6">
                <div className="text-sm uppercase tracking-[0.3em] text-amber-200/60 mb-3">{focusSessionLabel}</div>
                <div className="text-7xl md:text-8xl font-black font-mono text-amber-100 mb-4">
                  {formatTime(focusSecondsLeft)}
                </div>
                <div className="text-slate-600 mb-5">
                  Round {focusRound} of {selectedFocusPreset.rounds} • {focusMode === 'focus' ? 'Stay with one task' : 'Move, drink water, breathe'}
                </div>
                <div className="h-3 rounded-full bg-white overflow-hidden mb-6">
                  <div
                    className="h-full bg-amber-300 transition-all duration-500"
                    style={{ width: `${focusProgress}%` }}
                  />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-600 mb-6">
                  {focusTip}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {!focusRunning ? (
                    <button
                      onClick={startFocusTimer}
                      className="rounded-2xl bg-amber-300 py-4 text-black font-black col-span-2"
                    >
                      Start
                    </button>
                  ) : (
                    <button
                      onClick={pauseFocusTimer}
                      className="rounded-2xl bg-white py-4 text-black font-black col-span-2"
                    >
                      Pause
                    </button>
                  )}
                  <button
                    onClick={skipFocusPhase}
                    className="rounded-2xl border border-slate-200 bg-white/90 py-4 text-slate-600 hover:bg-violet-100"
                  >
                    Skip
                  </button>
                </div>
                <button
                  onClick={resetFocusTimer}
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 text-slate-500 hover:bg-violet-100"
                >
                  Reset focus session
                </button>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-[32px] border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-3">Distraction parking lot</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-5">
                  When your brain throws in another thought, park it here and return to the task.
                </p>
                <div className="flex gap-2 mb-4">
                  <input
                    value={focusDistraction}
                    onChange={(event) => setFocusDistraction(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') addDistraction();
                    }}
                    placeholder="Add distraction..."
                    className="min-w-0 flex-1 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-slate-700 placeholder:text-slate-400 outline-none focus:border-amber-400/40"
                  />
                  <button
                    onClick={addDistraction}
                    className="rounded-2xl bg-amber-300 text-black font-bold px-4"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {focusDistractions.length === 0 ? (
                    <div className="text-slate-400 text-sm">No distractions parked yet.</div>
                  ) : (
                    focusDistractions.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
                        <button
                          onClick={() => toggleDistraction(item.id)}
                          className={`w-6 h-6 rounded-full border flex-shrink-0 ${item.done ? 'bg-emerald-300 border-emerald-300' : 'border-white/20'}`}
                        />
                        <div className={`flex-1 text-sm ${item.done ? 'line-through text-slate-400' : 'text-slate-600'}`}>{item.text}</div>
                        <button onClick={() => removeDistraction(item.id)} className="text-slate-400 hover:text-rose-600">×</button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-[32px] border border-emerald-200 bg-emerald-100 p-6">
                <h3 className="text-xl font-black text-emerald-800 mb-3">ADHD-friendly rules</h3>
                <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
                  <p>One task only.</p>
                  <p>Start ugly. Improve later.</p>
                  <p>Breaks are part of the system, not failure.</p>
                  <p>Park distractions instead of chasing them.</p>
                </div>
              </div>
            </aside>
          </div>
        </section>
        </>)}

        {mainNav === 'journal' && (
        <>
        
        <section className="mb-16 rounded-[36px] border border-slate-200 bg-white/95 shadow-sm p-6 lg:p-10">
          <div className="grid xl:grid-cols-[1fr_420px] gap-8">
            <div>
              <div className="flex items-start justify-between gap-5 mb-8">
                <div>
                  <div className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">CBT Journal</div>
                  <h2 className="text-4xl font-black mb-3">Thought reframing system</h2>
                  <p className="text-slate-600 max-w-2xl leading-relaxed">
                    Capture a difficult moment, identify the automatic thought, check for thinking traps, then create a more balanced response.
                  </p>
                </div>

                <div className="hidden md:block rounded-3xl border border-violet-400/20 bg-violet-400/10 px-5 py-4 text-center min-w-32">
                  <div className="text-3xl font-black text-violet-300">{journalProgress}%</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">complete</div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5 mb-5">
                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">1. What happened?</div>
                  <textarea
                    value={journal.situation}
                    onChange={(event) => updateJournal('situation', event.target.value)}
                    placeholder="Example: My manager sent a short reply and I felt like I had done something wrong."
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>

                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">2. Automatic thought</div>
                  <textarea
                    value={journal.automaticThought}
                    onChange={(event) => updateJournal('automaticThought', event.target.value)}
                    placeholder="Example: I’m useless. They’re annoyed with me. I’ll lose my job."
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-5 mb-5">
                <div className="text-sm text-slate-500 mb-4">3. Emotions involved</div>
                <div className="flex flex-wrap gap-2">
                  {EMOTIONS.map((emotion) => {
                    const active = journal.emotions.includes(emotion);
                    return (
                      <button
                        key={emotion}
                        onClick={() => toggleJournalArray('emotions', emotion)}
                        className={`rounded-full border px-4 py-2 text-sm transition ${
                          active
                            ? 'border-teal-400 bg-teal-400/15 text-teal-700'
                            : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-violet-100'
                        }`}
                      >
                        {emotion}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-500">4. Intensity before</span>
                    <span className="font-mono text-orange-300">{journal.intensityBefore}/100</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={journal.intensityBefore}
                    onChange={(event) => updateJournal('intensityBefore', Number(event.target.value))}
                    className="w-full accent-orange-300"
                  />
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-slate-500">9. Intensity after</span>
                    <span className="font-mono text-emerald-800">{journal.intensityAfter}/100</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={journal.intensityAfter}
                    onChange={(event) => updateJournal('intensityAfter', Number(event.target.value))}
                    className="w-full accent-emerald-300"
                  />
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5 mb-5">
                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">5. Evidence supporting the thought</div>
                  <textarea
                    value={journal.evidenceFor}
                    onChange={(event) => updateJournal('evidenceFor', event.target.value)}
                    placeholder="What facts support the thought? Try to separate facts from feelings."
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>

                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">6. Evidence against the thought</div>
                  <textarea
                    value={journal.evidenceAgainst}
                    onChange={(event) => updateJournal('evidenceAgainst', event.target.value)}
                    placeholder="What facts suggest the thought may not be completely true?"
                    className="w-full min-h-32 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-5 mb-5">
                <div className="text-sm text-slate-500 mb-4">7. Thinking traps</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {DISTORTIONS.map((distortion) => {
                    const active = journal.distortions.includes(distortion.id);
                    return (
                      <button
                        key={distortion.id}
                        onClick={() => toggleJournalArray('distortions', distortion.id)}
                        className={`rounded-2xl border p-4 text-left transition ${
                          active
                            ? 'border-violet-400/40 bg-violet-400/15'
                            : 'border-slate-200 bg-slate-50 hover:bg-violet-100'
                        }`}
                      >
                        <div className={active ? 'font-bold text-violet-700 mb-1' : 'font-bold text-slate-600 mb-1'}>
                          {distortion.name}
                        </div>
                        <div className="text-xs text-slate-500 leading-relaxed">{distortion.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-5 mb-6">
                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">8. Balanced thought</div>
                  <textarea
                    value={journal.balancedThought}
                    onChange={(event) => updateJournal('balancedThought', event.target.value)}
                    placeholder="Example: I feel worried, but a short reply does not prove they are angry. I can ask for clarity if needed."
                    className="w-full min-h-36 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                  <button
                    type="button"
                    onClick={applyBalancedSuggestion}
                    className="mt-3 rounded-xl bg-violet-200 px-4 py-2 text-sm font-bold text-violet-900 hover:bg-violet-300"
                  >
                    Suggest balanced thought
                  </button>
                  {reframeMessage && (
                    <p className="mt-2 text-sm text-violet-700">{reframeMessage}</p>
                  )}
                </label>

                <label className="rounded-3xl border border-slate-200 bg-white/85 p-5">
                  <div className="text-sm text-slate-500 mb-3">10. Small next step</div>
                  <textarea
                    value={journal.actionStep}
                    onChange={(event) => updateJournal('actionStep', event.target.value)}
                    placeholder="Example: Take 3 slow breaths, then reply calmly or leave it until tomorrow."
                    className="w-full min-h-36 bg-white border border-slate-200 rounded-2xl p-4 text-slate-700 placeholder:text-slate-400 outline-none focus:border-teal-400 resize-none"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-3 clear-bottom-nav">
                <button
                  onClick={saveJournalEntry}
                  className="rounded-2xl bg-violet-400 text-black font-bold px-6 py-4 hover:scale-[1.02] transition-transform"
                >
                  Save CBT Entry
                </button>
                <button
                  onClick={newJournalEntry}
                  className="rounded-2xl border border-slate-200 bg-white/90 px-6 py-4 text-slate-600 hover:bg-violet-100 transition"
                >
                  New Entry
                </button>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="rounded-3xl border border-violet-400/20 bg-violet-400/10 p-6">
                <div className="text-sm text-violet-700/70 mb-2">Mood shift</div>
                <div className="text-5xl font-black text-violet-700 mb-2">-{journalReduction}</div>
                <div className="text-slate-500 text-sm">Intensity reduction after reframing</div>
                <div className="mt-5 h-3 rounded-full bg-white overflow-hidden">
                  <div
                    className="h-full bg-violet-300 transition-all"
                    style={{ width: `${journalReduction}%` }}
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
                <h3 className="text-2xl font-black mb-4">Saved entries</h3>
                {entries.length === 0 ? (
                  <p className="text-slate-500 leading-relaxed">No CBT entries yet. Save your first thought record to build your journal history.</p>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className={`rounded-2xl border p-4 ${
                          activeEntryId === entry.id
                            ? 'border-violet-400/40 bg-violet-400/10'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <button onClick={() => loadJournalEntry(entry)} className="text-left w-full">
                          <div className="text-xs text-slate-400 mb-2">
                            {new Date(entry.updatedAt).toLocaleString()}
                          </div>
                          <div className="font-bold mb-2 line-clamp-2">
                            {entry.situation || 'Untitled CBT entry'}
                          </div>
                          <div className="text-sm text-slate-500 line-clamp-2">
                            {entry.balancedThought || entry.automaticThought || 'No thought added yet'}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {(entry.emotions || []).slice(0, 3).map((emotion) => (
                              <span key={emotion} className="rounded-full border border-slate-200 bg-white/90 px-2 py-1 text-[11px] text-slate-500">
                                {emotion}
                              </span>
                            ))}
                          </div>
                        </button>
                        <button
                          onClick={() => deleteJournalEntry(entry.id)}
                          className="mt-3 text-xs text-rose-600 hover:text-rose-700"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/85 p-6">
                <h3 className="text-xl font-black mb-3">Helpful prompts</h3>
                <div className="space-y-3 text-sm text-slate-500 leading-relaxed">
                  <p>What would I say to a friend in this situation?</p>
                  <p>Is this thought 100% true, or just understandable because I feel stressed?</p>
                  <p>What is a more balanced explanation?</p>
                  <p>What small action would help me feel 5% better?</p>
                </div>
              </div>
            </aside>
          </div>
        </section>
        </>)}

        {routineRunnerOpen && routineRunnerStep && (
          <section className={`${overlayPanelClass} z-[55] bg-[#fafcff]/98 backdrop-blur-xl`}>
            <div ref={routineRunnerRef} className="min-h-screen max-w-3xl mx-auto px-5 py-8 flex flex-col">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <div className="text-sm uppercase tracking-[0.3em] text-emerald-600 mb-2">Guided routine</div>
                  <h2 className="text-3xl font-black">Step {routineRunnerStepIndex + 1} of {routineRunnerSteps.length}</h2>
                </div>
                <button type="button" onClick={closeRoutineRunner} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 font-bold text-slate-600">
                  Close
                </button>
              </div>

              <div className="rounded-[36px] border border-emerald-200 bg-emerald-50 p-6 md:p-8 flex-1">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{routineRunnerStep.type}</div>
                <h3 className="text-4xl font-black text-emerald-800 mb-4">{routineRunnerStep.tool}</h3>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">{routineRunnerStep.action}</p>

                <div className="text-center mb-8">
                  <div className="text-6xl font-black font-mono text-emerald-700">{formatTime(routineRunnerSecondsLeft)}</div>
                  <div className="text-sm text-slate-500 mt-2">{routineRunnerStep.minutes} minute step</div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {!routineRunnerRunning ? (
                    <button type="button" onClick={startRoutineRunnerTimer} className="rounded-2xl bg-emerald-300 py-4 font-black text-black col-span-2">
                      {routineRunnerSecondsLeft === 0 ? 'Complete step' : 'Start timer'}
                    </button>
                  ) : (
                    <button type="button" onClick={pauseRoutineRunnerTimer} className="rounded-2xl bg-white py-4 font-black text-black col-span-2">
                      Pause
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={previousRoutineRunnerStep} disabled={routineRunnerStepIndex === 0} className="rounded-2xl border border-slate-200 py-3 font-bold disabled:opacity-30">
                    Back
                  </button>
                  <button type="button" onClick={nextRoutineRunnerStep} className="rounded-2xl bg-teal-300 py-3 font-black text-teal-900">
                    {routineRunnerStepIndex >= routineRunnerSteps.length - 1 ? 'Finish' : 'Next step'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => openRoutineTool(routineRunnerStep)}
                  className="mt-4 w-full rounded-2xl border border-slate-200 bg-white py-3 text-sm font-bold text-slate-600"
                >
                  Open related tool
                </button>
              </div>
            </div>
          </section>
        )}

      </main>

      {!isImmersive && (
        <nav
          className="fixed bottom-0 inset-x-0 z-50 border-t border-slate-200 backdrop-blur-xl px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          style={{ background: 'var(--surface-header)' }}
          role="tablist"
          aria-label="Main navigation"
        >
          <div className="max-w-lg mx-auto grid grid-cols-4 gap-1">
            {MAIN_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={mainNav === item.id}
                onClick={() => setMainNav(item.id)}
                className={`rounded-2xl py-2.5 px-1 text-center transition ${
                  mainNav === item.id
                    ? 'bg-teal-300 text-teal-900'
                    : 'text-slate-500 hover:bg-violet-100'
                }`}
              >
                <div className="text-lg leading-none mb-1" aria-hidden="true">{item.icon}</div>
                <div className="text-[11px] font-bold">{item.label}</div>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
